import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../AuthContext'
import { API_BASE } from '../config'
import { photoUrl } from '../utils/photoUrl'
import DoctorSettings from './DoctorSettings'
import PatientDetails from './PatientDetails'

const C = {
  g900:'#0D2218', g800:'#1A3A28', g700:'#235233', g600:'#2D6B42',
  g500:'#3D8A57', g400:'#5AAE73', g300:'#85C99A', g100:'#E8F5EE', g50:'#F2FAF5',
  white:'#FFFFFF', text:'#1A2820', muted:'#5A7065', border:'#C8DDD2', error:'#C0392B',
  headerGrad:'linear-gradient(150deg,#0F3D24 0%,#1E6B3C 60%,#2E8B4F 100%)',
}

// Display a doctor's name with exactly one "Dr." prefix — the stored name may or
// may not already include it (avoids "Dr. Dr. Lakshmi Priya").
const docName = (n) => 'Dr. ' + (String(n || 'Doctor').replace(/^\s*dr\.?\s+/i, '').trim() || 'Doctor')

// Dashboard-shell palette (clean/light, matches the product mock)
const U = {
  bg:'#F6F8FB', card:'#FFFFFF', border:'#E8EDF3', text:'#1A2233', muted:'#6B7A90',
  primary:'#2563EB', primarySoft:'#EAF1FE',
}

const STATUS_COLORS = {
  pending:            { bg:'#FEF9F0', color:'#B7770D', border:'#FCEDC7' },
  confirmed:          { bg:'#EBF8F0', color:'#1A6B3C', border:'#A3DFB8' },
  completed:          { bg:'#EDF2FF', color:'#3451B2', border:'#BAC8FF' },
  cancelled:          { bg:'#FFF0F0', color:'#C92A2A', border:'#FFC9C9' },
  followup_suggested: { bg:'#F3F0FF', color:'#6741D9', border:'#D0BFFF' },
}

const fmtDate = d => {
  if (!d) return '—'
  try {
    const s = typeof d === 'string'
      ? (d.includes('T') ? d.split('T')[0] : d.split(' ')[0])
      : new Date(d).toISOString().slice(0,10)
    const [y,m,day] = s.split('-')
    return `${day}/${m}/${y}`
  } catch { return String(d) }
}

const parseArr = v => { try { return typeof v === 'string' ? JSON.parse(v || '[]') : (Array.isArray(v) ? v : []) } catch { return [] } }
const dateOnly = a => { const r = a?.date || ''; return r.includes('T') ? r.split('T')[0] : r.split(' ')[0] }

// Date-strip helpers (mobile Appointments day view)
const addDaysISO = (iso, n) => { const d = new Date(iso + 'T00:00:00'); d.setDate(d.getDate() + n); return d.toISOString().slice(0,10) }
const mondayOfISO = iso => { const d = new Date(iso + 'T00:00:00'); const wd = (d.getDay() + 6) % 7; d.setDate(d.getDate() - wd); return d.toISOString().slice(0,10) }
const weekdayShort = iso => new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' })
const dayMonShort  = iso => new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { day: 'numeric', month: 'short' })
// Parse an appointment's hour (0-23) from time/timeSlot strings like "4:30 PM" or "09:00"
const apptHour = a => {
  const t = (a?.time || a?.timeSlot || '').toString().trim()
  const m = t.match(/(\d{1,2})\s*:?\s*(\d{2})?\s*(am|pm)?/i)
  if (!m) return null
  let h = parseInt(m[1], 10); const ap = (m[3] || '').toLowerCase()
  if (ap === 'pm' && h < 12) h += 12
  if (ap === 'am' && h === 12) h = 0
  return isNaN(h) ? null : h
}

// Minutes past midnight for an appointment's slot, so two appointments on the
// same day can be told apart. Unparseable times sort to the start of the day.
const apptMinutes = a => {
  const t = (a?.time || a?.timeSlot || '').toString().trim()
  const m = t.match(/(\d{1,2})\s*:?\s*(\d{2})?\s*(am|pm)?/i)
  if (!m) return 0
  let h = parseInt(m[1], 10); const ap = (m[3] || '').toLowerCase()
  if (ap === 'pm' && h < 12) h += 12
  if (ap === 'am' && h === 12) h = 0
  return (isNaN(h) ? 0 : h) * 60 + (parseInt(m[2], 10) || 0)
}

/**
 * The order a doctor wants to read appointments in: what is still to come,
 * soonest first, then what has already happened, most recent first.
 *
 * The API returns them by when the BOOKING WAS MADE, which is nearly the same
 * thing right up until it isn't — a walk-in, a reschedule, or anything booked
 * days ahead lands in the middle of last week.
 */
const byAppointmentTime = list => {
  const key = a => `${String(a?.date || '')}T${String(apptMinutes(a)).padStart(4, '0')}`
  // The clinic's clock, not the browser's and not UTC: before 5:30am IST the
  // two disagree about what day it is, and "upcoming" would mean yesterday.
  const now = new Date()
  const istDate = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(now)
  const [h, mi] = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: false })
    .format(now).split(':').map(Number)
  const nowKey = `${istDate}T${String(h * 60 + mi).padStart(4, '0')}`
  const upcoming = [], past = []
  for (const a of list) (key(a) >= nowKey ? upcoming : past).push(a)
  upcoming.sort((x, y) => key(x).localeCompare(key(y)))
  past.sort((x, y) => key(y).localeCompare(key(x)))
  return [...upcoming, ...past]
}

function PhotosSection({ photos, apiBase, border, muted }) {
  let list = []
  try {
    if (typeof photos === 'string' && photos.trim().startsWith('[')) list = JSON.parse(photos)
    else if (Array.isArray(photos)) list = photos
  } catch(e) {}
  if (!list || list.length === 0) return null
  return (
    <div style={{ padding:'1rem 1.5rem', borderBottom:`1px solid ${border}` }}>
      <div style={{ fontSize:'.7rem', fontWeight:600, color:muted, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:'.75rem' }}>📷 Patient Photos</div>
      <div style={{ display:'flex', gap:'.6rem', flexWrap:'wrap' }}>
        {list.map((filename, i) => (
          <a key={i} href={photoUrl(filename)} target="_blank" rel="noopener noreferrer"
            style={{ display:'block', width:80, height:80, borderRadius:9, overflow:'hidden', border:`1.5px solid ${border}`, flexShrink:0, cursor:'pointer' }}>
            <img src={photoUrl(filename)} alt={`photo-${i+1}`}
              style={{ width:'100%', height:'100%', objectFit:'cover' }}
              onError={e => { e.target.style.display='none' }} />
          </a>
        ))}
      </div>
    </div>
  )
}

function ProgressPhotosSection({ progressPhotos, apptDate, apptId, apiBase, border, fetchWithAuth, onReviewed }) {
  let list = []
  try {
    if (typeof progressPhotos === 'string' && progressPhotos.trim().startsWith('[')) list = JSON.parse(progressPhotos)
    else if (Array.isArray(progressPhotos)) list = progressPhotos
  } catch(e) {}

  const [reviews, setReviews] = React.useState([])
  const [verdict, setVerdict] = React.useState('')
  const [note, setNote] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const [saved, setSaved] = React.useState(false)

  const loadReviews = React.useCallback(() => {
    if (!apptId || !fetchWithAuth) return
    fetchWithAuth(`${apiBase}/api/appointments/${apptId}/photo-reviews`)
      .then(r => r.ok ? r.json() : { reviews: [] })
      .then(d => setReviews(d.reviews || []))
      .catch(() => {})
  }, [apptId, fetchWithAuth, apiBase])
  React.useEffect(() => { loadReviews() }, [loadReviews])

  if (list.length === 0 && reviews.length === 0) return null

  const last = reviews[0] || null
  const newSince = last ? Math.max(0, list.length - (last.photoCount || 0)) : list.length
  const VERDICTS = [['improving','Improving','#2D6B42','#EBF8F0'],['same','Same','#B7770D','#FEF9F0'],['worse','Worse','#C92A2A','#FFF0F0']]
  const vMeta = v => VERDICTS.find(x => x[0] === v) || ['','—','#888','#eee']

  const save = async () => {
    if (!verdict || !apptId) return
    setSaving(true); setSaved(false)
    try {
      const res = await fetchWithAuth(`${apiBase}/api/appointments/${apptId}/photo-review`, {
        method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ verdict, note })
      })
      if (res.ok) { setSaved(true); setNote(''); setVerdict(''); setTimeout(()=>setSaved(false),2500); loadReviews(); onReviewed && onReviewed() }
    } catch(e) {} finally { setSaving(false) }
  }

  return (
    <div style={{ padding:'1rem 1.5rem', borderBottom:`1px solid ${border}`, background:'#F5F7FF' }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:'.6rem' }}>
        <div style={{ fontSize:'.7rem', fontWeight:600, color:'#3451B2', textTransform:'uppercase', letterSpacing:'.08em' }}>📸 Progress Photos ({list.length})</div>
        {newSince > 0 && <span style={{ fontSize:'.62rem', color:'#fff', background:'#C92A2A', padding:'.12rem .45rem', borderRadius:999, fontWeight:700 }}>{newSince} new to review</span>}
      </div>

      {/* Side-by-side compare: first vs latest */}
      {list.length >= 2 && (() => {
        const sorted = [...list].sort((a,b) => (parseInt(String(a).split('-')[0])||0) - (parseInt(String(b).split('-')[0])||0))
        const pair = [['First', sorted[0]], ['Latest', sorted[sorted.length-1]]]
        return (
          <div style={{ display:'flex', gap:10, marginBottom:'.85rem' }}>
            {pair.map(([lbl, fn]) => {
              const ts = parseInt(String(fn).split('-')[0]); const d = isNaN(ts) ? null : new Date(ts)
              return (
                <a key={lbl} href={photoUrl(fn)} target="_blank" rel="noopener noreferrer" style={{ flex:1, textDecoration:'none' }}>
                  <div style={{ width:'100%', height:150, borderRadius:10, overflow:'hidden', border:'1.5px solid #BAC8FF', background:'#fff' }}>
                    <img src={photoUrl(fn)} alt={lbl} style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e => { e.target.style.display='none' }} />
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', marginTop:4 }}>
                    <span style={{ fontSize:'.7rem', fontWeight:700, color:'#3451B2' }}>{lbl}</span>
                    <span style={{ fontSize:'.66rem', color:C.muted }}>{d ? fmtDate(d.toISOString().slice(0,10)) : ''}</span>
                  </div>
                </a>
              )
            })}
          </div>
        )
      })()}

      {list.length === 0 ? (
        <div style={{ fontSize:'.78rem', color:'#888', marginBottom:'.6rem' }}>No progress photos yet.</div>
      ) : (
        <div style={{ display:'flex', gap:'.6rem', flexWrap:'wrap', marginBottom:'.85rem' }}>
          {list.map((filename, i) => (
            <a key={i} href={photoUrl(filename)} target="_blank" rel="noopener noreferrer"
              style={{ display:'block', width:80, height:80, borderRadius:9, overflow:'hidden', border:'1.5px solid #BAC8FF', flexShrink:0, cursor:'pointer' }}>
              <img src={photoUrl(filename)} alt={`progress-${i+1}`}
                style={{ width:'100%', height:'100%', objectFit:'cover' }}
                onError={e => { e.target.style.display='none' }} />
            </a>
          ))}
        </div>
      )}

      {/* Last verdict */}
      {last && (
        <div style={{ fontSize:'.72rem', color:C.muted, marginBottom:'.7rem' }}>
          Last review: <span style={{ fontWeight:700, color:vMeta(last.verdict)[2] }}>{vMeta(last.verdict)[1]}</span> · {fmtDate(last.createdAt)}{last.note ? ` · "${last.note}"` : ''}
        </div>
      )}

      {/* Verdict control */}
      {list.length > 0 && (
        <div style={{ background:'#fff', border:`1px solid ${border}`, borderRadius:10, padding:'.8rem' }}>
          <div style={{ fontSize:'.68rem', fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'.55rem' }}>Review progress</div>
          <div style={{ display:'flex', gap:8, marginBottom:'.6rem' }}>
            {VERDICTS.map(([v,label,color,bg]) => (
              <button key={v} onClick={() => setVerdict(v)} style={{ flex:1, padding:'.5rem', borderRadius:8, border:`1.5px solid ${verdict===v?color:border}`, background:verdict===v?bg:'#fff', color:verdict===v?color:C.text, fontWeight:700, fontSize:'.78rem', cursor:'pointer', fontFamily:'inherit' }}>{label}</button>
            ))}
          </div>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="Note to patient (optional) — e.g. 'Redness reduced, keep the night cream'"
            style={{ width:'100%', padding:'.55rem .7rem', borderRadius:8, border:`1px solid ${border}`, fontSize:'.8rem', fontFamily:'inherit', resize:'vertical', outline:'none', boxSizing:'border-box', marginBottom:'.6rem' }} />
          <button onClick={save} disabled={!verdict || saving} style={{ width:'100%', padding:'.6rem', borderRadius:8, border:'none', background:saved?C.g500:(verdict?C.g700:'#ccc'), color:'#fff', fontWeight:700, fontSize:'.82rem', cursor:verdict&&!saving?'pointer':'default', fontFamily:'inherit' }}>
            {saving ? 'Sending…' : saved ? '✓ Sent to patient' : 'Send review to patient'}
          </button>
        </div>
      )}
    </div>
  )
}

const SEV_COLOR = { mild:'#2D6B42', moderate:'#B7770D', severe:'#C92A2A' }
const SEV_BG    = { mild:'#EBF8F0', moderate:'#FEF9F0', severe:'#FFF0F0' }
const COND_PALETTE = ['#7C3AED','#2563EB','#0891B2','#D97706','#DC2626','#DB2777','#059669','#EA580C']

function SkinScanSection({ patientEmail, apiBase, fetchWithAuth, appointmentDate, appointmentTime }) {
  const [scans, setScans]       = React.useState(null)
  const [loading, setLoading]   = React.useState(true)
  const [expanded, setExpanded] = React.useState(false)
  const [photoIdx, setPhotoIdx] = React.useState(0)

  // Only unlock 30 minutes before appointment
  const unlocked = React.useMemo(() => {
    if (!appointmentDate) return true // no date = always show
    try {
      const dateStr = (appointmentDate || '').split('T')[0].split(' ')[0]
      const timeStr = appointmentTime || '00:00'
      const apptMs  = new Date(`${dateStr}T${timeStr}:00`).getTime()
      if (isNaN(apptMs)) return true
      const minsUntil = (apptMs - Date.now()) / 60000
      return minsUntil <= 30  // show if within 30 min or past
    } catch { return true }
  }, [appointmentDate, appointmentTime])

  React.useEffect(() => {
    if (!patientEmail || !unlocked) { setLoading(false); return }
    setLoading(true); setScans(null); setPhotoIdx(0)
    fetchWithAuth(`${apiBase}/api/skin-scans?email=${encodeURIComponent(patientEmail)}`)
      .then(r => r.json())
      .then(d => setScans(d.scans || []))
      .catch(() => setScans([]))
      .finally(() => setLoading(false))
  }, [patientEmail, unlocked])

  if (!unlocked) {
    try {
      const dateStr  = (appointmentDate || '').split('T')[0].split(' ')[0]
      const timeStr  = appointmentTime || '00:00'
      const apptMs   = new Date(`${dateStr}T${timeStr}:00`).getTime()
      const minsLeft = Math.ceil((apptMs - Date.now()) / 60000)
      const hoursLeft = Math.floor(minsLeft / 60)
      const label = hoursLeft > 0 ? `${hoursLeft}h ${minsLeft % 60}m` : `${minsLeft}m`
      return (
        <div style={{ padding:'1rem 1.5rem', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:'1.1rem' }}>🔒</span>
          <div>
            <div style={{ fontSize:'.78rem', fontWeight:700, color:C.g800 }}>AI Skin Scan</div>
            <div style={{ fontSize:'.7rem', color:C.muted }}>Available {label} before appointment</div>
          </div>
        </div>
      )
    } catch {}
  }

  if (loading) return (
    <div style={{ padding:'1rem 1.5rem', borderBottom:`1px solid ${C.border}`, fontSize:'.8rem', color:C.muted }}>
      Loading AI skin scan…
    </div>
  )
  if (!scans || scans.length === 0) return (
    <div style={{ padding:'1rem 1.5rem', borderBottom:`1px solid ${C.border}` }}>
      <div style={{ fontSize:'.7rem', fontWeight:600, color:C.muted, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:'.4rem' }}>🔬 AI Skin Scan</div>
      <div style={{ fontSize:'.8rem', color:C.muted, fontStyle:'italic' }}>No scan on file — patient hasn't used AI Skin Scan yet.</div>
    </div>
  )

  const scan     = scans[0]
  const analysis = scan.analysis || {}
  const conds    = analysis.conditions || []
  const daysAgo  = Math.floor((Date.now() - new Date(scan.created_at).getTime()) / 86400000)
  const ageLabel = daysAgo === 0 ? 'Today' : daysAgo === 1 ? 'Yesterday' : `${daysAgo} days ago`

  return (
    <div style={{ borderBottom:`1px solid ${C.border}` }}>
      {/* Header */}
      <div style={{ padding:'1rem 1.5rem', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer', background: expanded ? C.g50 : 'white' }}
        onClick={() => setExpanded(e => !e)}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:'1.1rem' }}>🔬</span>
          <div>
            <div style={{ fontSize:'.78rem', fontWeight:700, color:C.g800 }}>AI Skin Scan</div>
            <div style={{ fontSize:'.68rem', color:C.muted }}>{ageLabel} · {analysis.skinType || '—'} skin · {conds.length} condition{conds.length !== 1 ? 's' : ''}</div>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          {analysis.topConcern && (
            <span style={{ fontSize:'.68rem', background:C.g100, color:C.g700, padding:'.2rem .6rem', borderRadius:999, fontWeight:600 }}>
              {analysis.topConcern}
            </span>
          )}
          <span style={{ color:C.muted, fontSize:'.8rem' }}>{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {expanded && (
        <div style={{ borderTop:`1px solid ${C.border}` }}>
          {/* Scan image */}
          {scan.image_path && (
            <div style={{ padding:'1rem 1.5rem 0' }}>
              <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:'.5rem' }}>
                <div style={{ fontSize:'.65rem', fontWeight:600, color:C.muted, textTransform:'uppercase', letterSpacing:'.08em' }}>Scan Photo</div>
              </div>
              <img
                src={`${apiBase}/${scan.image_path}`}
                alt="skin scan"
                style={{ width:'100%', maxHeight:220, objectFit:'cover', borderRadius:10, border:`1px solid ${C.border}` }}
                onError={e => e.target.style.display='none'}
              />
            </div>
          )}

          {/* Conditions */}
          {conds.length > 0 && (
            <div style={{ padding:'1rem 1.5rem 0' }}>
              <div style={{ fontSize:'.65rem', fontWeight:600, color:C.muted, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:'.5rem' }}>Detected Conditions</div>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {conds.map((c, i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'.5rem .75rem', borderRadius:8, background: SEV_BG[c.severity] || '#f9f9f9', border:`1px solid ${SEV_BG[c.severity] || '#eee'}` }}>
                    <div style={{ width:20, height:20, borderRadius:'50%', background:COND_PALETTE[i % COND_PALETTE.length], display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <span style={{ color:'white', fontSize:'.6rem', fontWeight:800 }}>{i+1}</span>
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <span style={{ fontWeight:600, fontSize:'.82rem', color:C.g900 }}>{c.name}</span>
                      {c.area && <span style={{ fontSize:'.72rem', color:C.muted }}> · {c.area.replace(/_/g,' ')}</span>}
                    </div>
                    <span style={{ fontSize:'.7rem', fontWeight:700, color: SEV_COLOR[c.severity] || C.muted, textTransform:'uppercase' }}>{c.severity}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Summary */}
          {analysis.summary && (
            <div style={{ padding:'1rem 1.5rem 0' }}>
              <div style={{ fontSize:'.65rem', fontWeight:600, color:C.muted, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:'.4rem' }}>AI Summary</div>
              <div style={{ fontSize:'.8rem', color:C.g800, lineHeight:1.55, background:C.g50, borderRadius:8, padding:'.7rem .9rem' }}>{analysis.summary}</div>
            </div>
          )}

          {/* Urgency + skin type footer */}
          <div style={{ padding:'1rem 1.5rem', display:'flex', gap:8, flexWrap:'wrap' }}>
            {analysis.skinType && (
              <span style={{ fontSize:'.72rem', background:'#EDF2FF', color:'#3451B2', padding:'.3rem .7rem', borderRadius:999, fontWeight:600 }}>
                Skin: {analysis.skinType}
              </span>
            )}
            {analysis.consultUrgency && (
              <span style={{ fontSize:'.72rem', background: analysis.consultUrgency==='urgent'?'#FFF0F0': analysis.consultUrgency==='soon'?'#FEF9F0':'#EBF8F0',
                color: analysis.consultUrgency==='urgent'?'#C92A2A': analysis.consultUrgency==='soon'?'#B7770D':'#1A6B3C',
                padding:'.3rem .7rem', borderRadius:999, fontWeight:600 }}>
                {analysis.consultUrgency==='urgent'?'⚠️ See urgently': analysis.consultUrgency==='soon'?'📅 See soon':'✓ Routine'}
              </span>
            )}
            <span style={{ fontSize:'.68rem', color:C.muted, marginLeft:'auto', alignSelf:'center' }}>
              Scan: {new Date(scan.created_at).toLocaleDateString('en-IN')}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

function StandaloneRxSection({ patientEmail, apiBase, fetchWithAuth, border }) {
  const [rxList, setRxList] = React.useState([])
  const [open, setOpen]     = React.useState(false)
  const [loading, setLoading] = React.useState(false)

  React.useEffect(() => {
    if (!patientEmail) return
    setLoading(true)
    fetchWithAuth(`${apiBase}/api/prescriptions?email=${encodeURIComponent(patientEmail)}`)
      .then(r => r.json())
      .then(d => setRxList(d.prescriptions || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [patientEmail])

  if (loading) return (
    <div style={{ padding:'1rem 1.5rem', borderBottom:`1px solid ${border}`, fontSize:'.8rem', color:'#888' }}>Loading patient prescriptions…</div>
  )
  if (rxList.length === 0) return null

  return (
    <div style={{ padding:'1rem 1.5rem', borderBottom:`1px solid ${border}`, background:'#FFFBF0' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: open ? '.75rem' : 0 }}>
        <div style={{ fontSize:'.7rem', fontWeight:600, color:'#B7770D', textTransform:'uppercase', letterSpacing:'.08em' }}>
          📋 Patient's Own Prescriptions ({rxList.length})
        </div>
        <button onClick={() => setOpen(o => !o)}
          style={{ fontSize:'.68rem', color:'#B7770D', background:'#FCEDC7', border:'1px solid #F5CC73', borderRadius:6, padding:'.2rem .55rem', cursor:'pointer', fontWeight:600 }}>
          {open ? 'Hide' : 'View'}
        </button>
      </div>
      {open && (
        <div style={{ display:'flex', flexDirection:'column', gap:'.6rem' }}>
          {rxList.map(rx => (
            <div key={rx.id} style={{ border:`1px solid #F5CC73`, borderRadius:10, overflow:'hidden', background:'white' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'.7rem .9rem' }}>
                {rx.imageFilename && (
                  <img src={photoUrl(rx.imageFilename)} alt="rx"
                    style={{ width:46, height:46, borderRadius:8, objectFit:'cover', border:`1px solid ${border}`, flexShrink:0 }}
                    onError={e => { e.target.style.display='none' }} />
                )}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:'.82rem', color:C.text }}>
                    Uploaded {new Date(rx.createdAt).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}
                  </div>
                  <div style={{ fontSize:'.7rem', color:'#888', marginTop:'.1rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {rx.medicationText ? rx.medicationText.slice(0, 80) + (rx.medicationText.length > 80 ? '…' : '') : 'No text extracted'}
                  </div>
                </div>
                {rx.imageFilename && (
                  <a href={photoUrl(rx.imageFilename)} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize:'.68rem', color:C.g600, background:C.g50, border:`1px solid ${border}`, borderRadius:6, padding:'.2rem .55rem', textDecoration:'none', whiteSpace:'nowrap', fontWeight:600 }}>
                    View Image
                  </a>
                )}
              </div>
              {rx.medicationText && (
                <div style={{ padding:'.5rem .9rem .7rem', borderTop:`1px solid #F5CC73`, background:'#FFFDF5' }}>
                  <div style={{ fontSize:'.62rem', fontWeight:700, color:'#B7770D', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:'.3rem' }}>Medication Text</div>
                  <pre style={{ margin:0, fontSize:'.76rem', color:C.text, whiteSpace:'pre-wrap', lineHeight:1.7, fontFamily:"'DM Sans',sans-serif" }}>{rx.medicationText}</pre>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const inp = { width:'100%', padding:'.75rem .9rem', border:`1.5px solid ${C.border}`, borderRadius:9, fontSize:'.85rem', outline:'none', fontFamily:"'DM Sans',sans-serif", color:C.text, resize:'vertical', boxSizing:'border-box', lineHeight:1.6, background:'white', transition:'border-color .2s' }

// ── Dashboard-shell config ─────────────────────────────────────────────────────
const NAV = [
  { icon:'🏠', label:'Dashboard' },
  { icon:'👥', label:'Patients' },
  { icon:'📅', label:'Appointments' },
  { icon:'🔔', label:'Alerts' },
  { icon:'💬', label:'Messages' },
  { icon:'📹', label:'Medications' },
  { icon:'🏷️', label:'Label Data' },
  { icon:'⚙️', label:'Profile' },
]
// Cards live in one scroll view; sidebar entries jump to the relevant section.
const NAV_TARGET = { Dashboard:'dash-top', Appointments:'appt-list', Patients:'appt-list', Alerts:'alerts-card' }

// Patient list columns. With the detail panel open there is no room for the
// middle two, so the list falls back to who they are and where they stand.
const PT_WIDE   = '2fr 1fr 1.4fr 1.2fr .9fr'
const PT_NARROW = '2fr 1fr .9fr'

const PATIENT_STATUS = {
  'Ongoing':   { bg:'#EBF8F0', color:'#1A6B3C', border:'#A3DFB8' },
  'Follow Up': { bg:'#FEF3E2', color:'#B7770D', border:'#FCEDC7' },
  'Completed': { bg:'#EDF2FF', color:'#3451B2', border:'#BAC8FF' },
}

// One person, as the doctor needs to see them. Everything here comes from
// appointments already in memory, so moving down the list is instant - there is
// no request per patient.
//
// Deliberately not a copy of the admin patient drawer: admin needs adherence
// percentages and reminder logs. A doctor needs four things - what I did last
// time, what they are on now, when I see them next, and what they came in
// saying.
function PatientPanel({ patient, onOpenAppt, onClose }) {
  const [tab, setTab] = React.useState('overview')
  React.useEffect(() => { setTab('overview') }, [patient.key])

  const latest   = patient.latestAppt || {}
  const rx       = latest.prescription || latest.medication || ''
  const sc       = PATIENT_STATUS[patient.status] || PATIENT_STATUS.Completed
  const concerns = patient.appts.filter(a => a.concern)

  const Field = ({ label, value }) => (
    <div>
      <div style={{ fontSize:'.62rem', fontWeight:600, color:U.muted, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:'.2rem' }}>{label}</div>
      <div style={{ fontSize:'.84rem', color:U.text, fontWeight:500 }}>{value || '\u2014'}</div>
    </div>
  )

  return (
    <div style={{ width:370, flexShrink:0, borderLeft:`1px solid ${U.border}`, alignSelf:'stretch', display:'flex', flexDirection:'column', maxHeight:'80vh' }}>
      <div style={{ padding:'1.1rem 1.15rem', borderBottom:`1px solid ${U.border}` }}>
        <div style={{ display:'flex', alignItems:'flex-start', gap:'.75rem' }}>
          <div style={{ width:44, height:44, borderRadius:'50%', background:`linear-gradient(135deg,${C.g700},${C.g500})`, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontFamily:"'DM Serif Display',serif", fontSize:'1.1rem', flexShrink:0 }}>
            {(patient.name||'?')[0].toUpperCase()}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:'1rem', fontWeight:700, color:U.text }}>{patient.name}</div>
            <div style={{ fontSize:'.73rem', color:U.muted, marginTop:2 }}>{patient.phone || '\u2014'}</div>
            {patient.email && <div style={{ fontSize:'.73rem', color:U.muted, overflow:'hidden', textOverflow:'ellipsis' }}>{patient.email}</div>}
          </div>
          <button onClick={onClose} aria-label="Close"
            style={{ background:'none', border:'none', color:U.muted, fontSize:'1.2rem', cursor:'pointer', lineHeight:1, padding:0 }}>&times;</button>
        </div>
        <div style={{ marginTop:'.7rem', display:'flex', gap:'.45rem', alignItems:'center' }}>
          <span style={{ fontSize:'.68rem', fontWeight:700, padding:'.2rem .6rem', borderRadius:999, background:sc.bg, color:sc.color, border:`1px solid ${sc.border}` }}>{patient.status}</span>
          <span style={{ fontSize:'.7rem', color:U.muted }}>{patient.visits} visit{patient.visits===1?'':'s'}</span>
        </div>
      </div>

      <div style={{ display:'flex', gap:2, padding:'.55rem .7rem', borderBottom:`1px solid ${U.border}` }}>
        {[['overview','Overview'],['history','Treatment History'],['notes','Notes']].map(([k,lbl]) => (
          <button key={k} onClick={() => setTab(k)}
            style={{ flex:1, padding:'.4rem .25rem', borderRadius:8, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:'.71rem', fontWeight:700, background: tab===k ? U.primarySoft : 'transparent', color: tab===k ? U.primary : U.muted }}>{lbl}</button>
        ))}
      </div>

      <div style={{ padding:'1rem 1.15rem', overflowY:'auto' }}>
        {tab === 'overview' && (
          <div style={{ display:'flex', flexDirection:'column', gap:'.9rem' }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'.85rem' }}>
              <Field label="Last visit"       value={patient.lastVisit ? fmtDate(patient.lastVisit) : 'No visit yet'} />
              <Field label="Treatment"        value={patient.service} />
              <Field label="Next appointment" value={patient.nextDate ? fmtDate(patient.nextDate) : 'Not booked'} />
              <Field label="Follow-up due"    value={patient.followUpDate ? fmtDate(patient.followUpDate) : '\u2014'} />
            </div>
            {patient.diagnosis && (
              <div style={{ background:'#EDF2FF', border:'1px solid #BAC8FF', borderRadius:10, padding:'.8rem .9rem' }}>
                <div style={{ fontSize:'.62rem', fontWeight:700, color:'#3451B2', textTransform:'uppercase', letterSpacing:'.07em', marginBottom:'.35rem' }}>Latest diagnosis</div>
                <div style={{ fontSize:'.82rem', color:'#1A2155', lineHeight:1.65, whiteSpace:'pre-wrap' }}>{patient.diagnosis}</div>
              </div>
            )}
            {rx && (
              <div style={{ background:'#F0FFF4', border:'1px solid #A3DFB8', borderRadius:10, padding:'.8rem .9rem' }}>
                <div style={{ fontSize:'.62rem', fontWeight:700, color:'#1A6B3C', textTransform:'uppercase', letterSpacing:'.07em', marginBottom:'.35rem' }}>Current prescription</div>
                <div style={{ fontSize:'.82rem', color:U.text, lineHeight:1.75, whiteSpace:'pre-wrap' }}>{rx}</div>
              </div>
            )}
            {latest.id && (
              <button onClick={() => onOpenAppt(latest)}
                style={{ padding:'.6rem', borderRadius:10, border:`1px solid ${U.border}`, background:U.bg, color:U.primary, fontWeight:700, fontSize:'.78rem', cursor:'pointer', fontFamily:'inherit' }}>
                Open latest consultation &rarr;
              </button>
            )}
          </div>
        )}

        {tab === 'history' && (
          <div style={{ display:'flex', flexDirection:'column' }}>
            {patient.appts.map((a, i) => {
              const line = (a.diagnosis || a.concern || '').split('\n')[0]
              return (
                <button key={a.id || i} onClick={() => onOpenAppt(a)}
                  style={{ display:'flex', gap:'.7rem', textAlign:'left', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', padding:0, width:'100%' }}>
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', flexShrink:0 }}>
                    <div style={{ width:9, height:9, borderRadius:'50%', border:`2px solid ${U.primary}`, background:U.card, marginTop:5 }} />
                    {i < patient.appts.length - 1 && <div style={{ flex:1, width:1, background:U.border, minHeight:26 }} />}
                  </div>
                  <div style={{ paddingBottom:'1rem', minWidth:0 }}>
                    <div style={{ fontSize:'.78rem', fontWeight:700, color:U.text }}>{fmtDate(a.date)}</div>
                    <div style={{ fontSize:'.78rem', color:U.text, marginTop:1 }}>{a.service || 'Consultation'}</div>
                    {line && <div style={{ fontSize:'.73rem', color:U.muted, marginTop:2, lineHeight:1.5 }}>{line}</div>}
                  </div>
                </button>
              )
            })}
          </div>
        )}

        {tab === 'notes' && (
          concerns.length === 0
            ? <div style={{ fontSize:'.8rem', color:U.muted }}>Nothing recorded from the patient yet.</div>
            : <div style={{ display:'flex', flexDirection:'column', gap:'.7rem' }}>
                {concerns.map((a, i) => (
                  <div key={a.id || i} style={{ background:U.bg, borderRadius:10, padding:'.7rem .8rem' }}>
                    <div style={{ fontSize:'.66rem', fontWeight:700, color:U.muted, marginBottom:'.25rem' }}>{fmtDate(a.date)}</div>
                    <div style={{ fontSize:'.8rem', color:U.text, lineHeight:1.6, whiteSpace:'pre-wrap' }}>{a.concern}</div>
                  </div>
                ))}
              </div>
        )}
      </div>
    </div>
  )
}

// Slice 2b: doctor-lane escalations (side effect / wants to stop) reported by patients.
function DoctorEscalations() {
  const { fetchWithAuth } = useAuth()
  const [items, setItems] = React.useState([])
  const [busy, setBusy] = React.useState(null)
  const load = React.useCallback(() => {
    fetchWithAuth(`${API_BASE}/api/doctor/med-issues?status=open`).then(r => r.ok ? r.json() : { issues: [] }).then(d => setItems(d.issues || [])).catch(() => setItems([]))
  }, [fetchWithAuth])
  React.useEffect(() => { load() }, [load])
  const RLABEL = { side_effect: 'Side effect / reaction', want_to_stop: 'Wants to stop the medicine' }
  const slotIcon = s => s === 'morning' ? '🌅' : s === 'afternoon' ? '☀️' : s === 'night' ? '🌙' : ''
  const [drafts, setDrafts] = React.useState({})
  const remove = id => setItems(prev => prev.filter(i => i.id !== id))
  const reviewed = async (id) => {
    setBusy(id)
    try { await fetchWithAuth(`${API_BASE}/api/med-issue/${id}/resolve`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }); remove(id) } catch {} finally { setBusy(null) }
  }
  const reply = async (id, action) => {
    const message = (drafts[id] || '').trim()
    if (action === 'replied' && !message) return
    setBusy(id)
    try { await fetchWithAuth(`${API_BASE}/api/med-issue/${id}/reply`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message, action }) }); remove(id) } catch {} finally { setBusy(null) }
  }
  if (!items.length) return null
  return (
    <div style={{ background:'#FFF5F5', border:'1px solid #F5C6C6', borderRadius:14, padding:'1rem 1.15rem', marginBottom:'1.25rem' }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:'.7rem' }}>
        <span style={{ fontSize:'1.05rem' }}>⚠️</span>
        <span style={{ fontWeight:800, color:'#B91C1C', fontSize:'.95rem' }}>Patient escalations needing your review ({items.length})</span>
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
        {items.map(it => {
          const free = it.withinWindow
          return (
            <div key={it.id} style={{ background:'#fff', border:'1px solid #F5C6C6', borderRadius:10, padding:'.85rem 1rem' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:'.55rem' }}>
                <div style={{ minWidth:150 }}>
                  <div style={{ fontWeight:700, color:'#1E293B', fontSize:'.9rem' }}>{it.patientName || it.patientEmail}</div>
                  <div style={{ fontSize:'.72rem', color:'#64748B' }}>{it.patientEmail}</div>
                </div>
                <div style={{ flex:1, minWidth:180 }}>
                  <div style={{ fontWeight:700, color:'#B91C1C', fontSize:'.85rem' }}>{RLABEL[it.reason] || it.reason}</div>
                  <div style={{ fontSize:'.75rem', color:'#64748B' }}>{slotIcon(it.slot)} {it.medicine || 'medication'}{it.note ? ` — "${it.note}"` : ''}</div>
                </div>
                <span title={it.apptDate ? `Consult: ${it.apptDate}` : ''} style={{ fontSize:'.66rem', fontWeight:700, padding:'2px 9px', borderRadius:999, background: free ? '#EBF8F0' : '#FFF8E1', color: free ? '#1A6B3C' : '#B7770D', whiteSpace:'nowrap' }}>
                  {free ? '🟢 Free support (≤7 days)' : '🟠 Out of window'}
                </span>
              </div>
              <textarea value={drafts[it.id] || ''} onChange={e => setDrafts(d => ({ ...d, [it.id]: e.target.value }))} rows={2}
                placeholder="Reply to the patient (they get it in the app + WhatsApp)…"
                style={{ width:'100%', boxSizing:'border-box', border:'1px solid #E2E8F0', borderRadius:8, padding:'.5rem .7rem', fontSize:'.82rem', fontFamily:'inherit', resize:'vertical' }} />
              <div style={{ display:'flex', gap:8, marginTop:'.5rem', flexWrap:'wrap' }}>
                <button onClick={() => reply(it.id, 'replied')} disabled={busy === it.id || !(drafts[it.id] || '').trim()}
                  style={{ padding:'.45rem .9rem', borderRadius:8, border:'none', background:'#1E6B3C', color:'#fff', fontSize:'.78rem', fontWeight:700, cursor:'pointer', opacity: (drafts[it.id] || '').trim() ? 1 : .5 }}>Send reply</button>
                <button onClick={() => reply(it.id, 'follow_up')} disabled={busy === it.id}
                  style={{ padding:'.45rem .9rem', borderRadius:8, border:'1px solid #C7D2FE', background:'#EEF2FF', color:'#3730A3', fontSize:'.78rem', fontWeight:700, cursor:'pointer' }}>Ask to book follow-up</button>
                <button onClick={() => reviewed(it.id)} disabled={busy === it.id}
                  style={{ padding:'.45rem .9rem', borderRadius:8, border:'1px solid #E2E8F0', background:'#fff', color:'#64748B', fontSize:'.78rem', fontWeight:600, cursor:'pointer' }}>Mark reviewed</button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function Card({ title, action, children, id, pad = true }) {
  return (
    <div id={id} style={{ background:U.card, border:`1px solid ${U.border}`, borderRadius:16, boxShadow:'0 1px 2px rgba(16,24,40,.04)' }}>
      {title && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'1rem 1.25rem', borderBottom:`1px solid ${U.border}` }}>
          <div style={{ fontSize:'.92rem', fontWeight:700, color:U.text }}>{title}</div>
          {action}
        </div>
      )}
      <div style={{ padding: pad ? '1.25rem' : 0 }}>{children}</div>
    </div>
  )
}

// ── Doctor self-service profile editor (name / email / phone) ─────────────────
function ProfileModal({ apiBase, fetchWithAuth, user, updateUser, onClose }) {
  const [form, setForm]     = React.useState({ name: user?.name || '', email: user?.email || '', phone: '' })
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving]   = React.useState(false)
  const [msg, setMsg]         = React.useState('')
  const [err, setErr]         = React.useState('')

  React.useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const res = await fetchWithAuth(`${apiBase}/api/doctor/profile`)
        const data = await res.json()
        if (alive && res.ok && data.profile) {
          setForm({ name: data.profile.name || '', email: data.profile.email || '', phone: data.profile.phone || '' })
        }
      } catch (e) { /* keep JWT-derived defaults */ }
      finally { if (alive) setLoading(false) }
    })()
    return () => { alive = false }
  }, [apiBase, fetchWithAuth])

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const save = async (e) => {
    e && e.preventDefault()
    setErr(''); setMsg('')
    if (!form.name.trim()) { setErr('Name is required.'); return }
    if (form.phone && form.phone.replace(/\D/g, '').length < 10) { setErr('Enter a valid mobile number.'); return }
    setSaving(true)
    try {
      const res = await fetchWithAuth(`${apiBase}/api/doctor/profile`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim() }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message || 'Could not save changes.')
      updateUser && updateUser({ name: data.profile.name, email: data.profile.email })
      if (data.emailChanged) {
        setMsg('Saved. Your email changed — please log out and sign in again for it to fully apply.')
      } else {
        setMsg('Profile updated ✓')
        setTimeout(() => onClose(), 900)
      }
    } catch (e) { setErr(e.message) }
    finally { setSaving(false) }
  }

  const field = { width:'100%', padding:'.7rem 1rem', border:`1.5px solid ${U.border}`, borderRadius:9, fontSize:'.875rem', outline:'none', fontFamily:'inherit', boxSizing:'border-box', color:U.text }
  const label = { fontSize:'.72rem', fontWeight:600, color:U.muted, display:'block', marginBottom:'.3rem', textTransform:'uppercase', letterSpacing:'.05em' }

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:2000, background:'rgba(10,20,15,.45)', display:'flex', alignItems:'center', justifyContent:'center', padding:'1.5rem' }}>
      <div onClick={e => e.stopPropagation()} style={{ background:U.card, borderRadius:18, padding:'1.75rem', width:'100%', maxWidth:440, boxShadow:'0 12px 40px rgba(16,24,40,.18)', fontFamily:"'DM Sans',sans-serif" }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1.25rem' }}>
          <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.25rem', color:U.text }}>Edit profile</div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:'1.3rem', color:U.muted, cursor:'pointer', lineHeight:1 }}>×</button>
        </div>

        {loading ? (
          <div style={{ padding:'1.5rem 0', textAlign:'center', color:U.muted, fontSize:'.85rem' }}>Loading…</div>
        ) : (
          <form onSubmit={save} style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
            <div>
              <label style={label}>Full name</label>
              <input value={form.name} onChange={e => set('name', e.target.value)} style={field} placeholder="Dr. Name" />
            </div>
            <div>
              <label style={label}>Email</label>
              <input type="email" value={form.email} onChange={e => set('email', e.target.value)} style={field} placeholder="you@revivehc.in" />
            </div>
            <div>
              <label style={label}>Mobile number</label>
              <input type="tel" inputMode="numeric" value={form.phone} onChange={e => set('phone', e.target.value)} style={field} placeholder="9XXXXXXXXX" />
              <div style={{ fontSize:'.72rem', color:U.muted, marginTop:'.3rem' }}>This is the number you use to sign in with OTP.</div>
            </div>
            {err && <div style={{ fontSize:'.8rem', color:'#C0392B', background:'#FFF0F0', border:'1px solid #FFC9C9', borderRadius:9, padding:'.6rem .8rem' }}>{err}</div>}
            {msg && <div style={{ fontSize:'.8rem', color:C.g700, background:C.g50, border:`1px solid ${C.border}`, borderRadius:9, padding:'.6rem .8rem' }}>{msg}</div>}
            <div style={{ display:'flex', gap:'.6rem', marginTop:'.25rem' }}>
              <button type="button" onClick={onClose} style={{ flex:1, padding:'.8rem', borderRadius:10, background:U.bg, border:`1px solid ${U.border}`, color:U.muted, fontSize:'.875rem', fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
              <button type="submit" disabled={saving} style={{ flex:2, padding:'.8rem', borderRadius:10, background: saving ? C.g400 : C.g700, color:'#fff', border:'none', fontSize:'.875rem', fontWeight:700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily:'inherit' }}>{saving ? 'Saving…' : 'Save changes'}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default function DoctorDashboard() {
  const { user, fetchWithAuth, updateUser, logout } = useAuth()
  const [settingsOpen, setSettingsOpen] = React.useState(false)
  const [docPhoto, setDocPhoto] = React.useState(null)
  React.useEffect(() => {
    if (!user?.token) return
    fetchWithAuth(`${API_BASE}/api/doctor/profile`).then(r => r.json()).then(d => setDocPhoto(d.profile && d.profile.photo || null)).catch(() => {})
  }, [user?.token, settingsOpen]) // eslint-disable-line react-hooks/exhaustive-deps
  // AI Assistant — assisted reminder queue (empty/hidden unless AGENT_ENABLED on the server)
  const [agentTasks, setAgentTasks] = React.useState(null) // {enabled, tasks} | null
  const [agentBusy, setAgentBusy] = React.useState(false)
  const loadAgentTasks = React.useCallback(() => {
    fetchWithAuth(`${API_BASE}/api/agent/tasks`).then(r => r.json()).then(setAgentTasks).catch(() => setAgentTasks({ enabled: false, tasks: [] }))
  }, [fetchWithAuth])
  React.useEffect(() => { if (user?.token) loadAgentTasks() }, [user?.token, loadAgentTasks])
  const agentAct = async (id, verb) => {
    setAgentBusy(true)
    try { await fetchWithAuth(`${API_BASE}/api/agent/tasks/${id}/${verb}`, { method: 'POST' }) } catch (_) {}
    setAgentBusy(false); loadAgentTasks()
  }
  const agentScan = async () => {
    setAgentBusy(true)
    try { await fetchWithAuth(`${API_BASE}/api/agent/sweep`, { method: 'POST' }) } catch (_) {}
    setAgentBusy(false); loadAgentTasks()
  }
  const navigate = useNavigate()
  const [appointments, setAppointments] = React.useState([])
  const [loading, setLoading]           = React.useState(true)
  const [search, setSearch]             = React.useState('')
  const [statusFilter, setStatusFilter] = React.useState('all')
  const [selected, setSelected]         = React.useState(null)
  const [saving, setSaving]             = React.useState(false)
  const [saveMsg, setSaveMsg]           = React.useState('')
  const [form, setForm] = React.useState({ status:'', diagnosis:'', prescription:'', medication:'', followUpDate:'' })
  const [pending, setPending] = React.useState([])
  const [aiAlerts, setAiAlerts] = React.useState([])
  const [conditionByEmail, setConditionByEmail] = React.useState({})
  const [isMobile, setIsMobile] = React.useState(typeof window !== 'undefined' && window.innerWidth <= 860)
  const [drawerOpen, setDrawerOpen] = React.useState(false)
  const [messages, setMessages] = React.useState([])
  const [selectedMsg, setSelectedMsg] = React.useState(null)
  const [patientModal, setPatientModal] = React.useState(null)
  const [patientFilter, setPatientFilter] = React.useState('All')
  const [selPatient, setSelPatient] = React.useState(null)
  const [replyText, setReplyText] = React.useState('')
  const [replySaving, setReplySaving] = React.useState(false)
  const [aiDrafting, setAiDrafting] = React.useState(false)
  const [profileOpen, setProfileOpen] = React.useState(false)
  const _todayISO0 = new Date().toISOString().slice(0,10)
  const [weekStart, setWeekStart] = React.useState(() => mondayOfISO(_todayISO0))
  const [selDay, setSelDay] = React.useState(_todayISO0)
  // On first load, if today has no appointments, jump the day view to the most
  // recent day that does — so it isn't confusingly empty.
  const _daySetRef = React.useRef(false)
  React.useEffect(() => {
    if (_daySetRef.current || appointments.length === 0) return
    _daySetRef.current = true
    const dates = appointments.map(a => dateOnly(a)).filter(Boolean)
    if (!dates.includes(_todayISO0)) {
      const past = dates.filter(d => d <= _todayISO0).sort()
      const pick = past.length ? past[past.length - 1] : dates.sort()[dates.length - 1]
      if (pick) { setSelDay(pick); setWeekStart(mondayOfISO(pick)) }
    }
  }, [appointments]) // eslint-disable-line react-hooks/exhaustive-deps
  const [msgTab, setMsgTab] = React.useState('all')

  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 860px)')
    const onChange = () => { setIsMobile(mq.matches); if (!mq.matches) setDrawerOpen(false) }
    onChange()
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange)
    return () => { mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange) }
  }, [])
  const [activeNav, setActiveNav] = React.useState('Dashboard')

  React.useEffect(() => {
    if (!user?.token) { navigate('/login?redirect=/doctor-dashboard'); return }
    load()
    loadPending()
    loadAlerts()
    loadMessages()
  }, [user])

  const load = async () => {
    setLoading(true)
    try {
      const res  = await fetchWithAuth(`${API_BASE}/api/appointments`)
      const data = await res.json()
      setAppointments(byAppointmentTime(data.appointments || []))
    } catch {}
    finally { setLoading(false) }
  }

  const loadPending = async () => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/doctor/pending-reviews`)
      const data = await res.json()
      setPending(Array.isArray(data.pending) ? data.pending : [])
    } catch {}
  }

  const loadAlerts = async () => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/doctor/alerts`)
      const data = await res.json()
      setAiAlerts(Array.isArray(data.alerts) ? data.alerts : [])
      setConditionByEmail(data.conditionByEmail || {})
    } catch {}
  }

  const loadMessages = async () => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/doctor/messages`)
      const data = await res.json()
      setMessages(Array.isArray(data.messages) ? data.messages : [])
    } catch {}
  }

  const openMsg = m => { setSelectedMsg(m); setReplyText(m.replyText || '') }

  const sendReply = async () => {
    if (!selectedMsg || !replyText.trim()) return
    setReplySaving(true)
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/feedback/reply`, {
        method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ feedbackId: selectedMsg.id, replyText })
      })
      if (!res.ok) throw new Error()
      const repliedAt = new Date().toISOString()
      setMessages(prev => prev.map(m => m.id === selectedMsg.id ? { ...m, replyText, repliedAt } : m))
      setSelectedMsg(null)
    } catch(e) { alert('Could not send reply') }
    finally { setReplySaving(false) }
  }

  const aiDraft = async () => {
    if (!selectedMsg) return
    setAiDrafting(true)
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/ai/generate-reply`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ feedbackText: selectedMsg.feedbackText, patientName: selectedMsg.patientName, medication: selectedMsg.appt?.medication, prescription: selectedMsg.appt?.prescription })
      })
      const data = await res.json()
      if (data.reply) setReplyText(data.reply)
    } catch(e) {} finally { setAiDrafting(false) }
  }

  const open = appt => {
    setSelected(appt)
    setSaveMsg('')
    setForm({
      status:      appt.status      || 'pending',
      diagnosis:   appt.diagnosis   || '',
      prescription:appt.prescription|| '',
      medication:  appt.medication  || '',
      followUpDate:appt.followUpDate|| '',
    })
  }

  const save = async () => {
    if (!selected) return
    setSaving(true); setSaveMsg('')
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/appointments/${selected.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type':'application/json' },
        body: JSON.stringify(form),
      })
      if (!res.ok) throw new Error()
      setSaveMsg('success')
      setAppointments(prev => prev.map(a => a.id === selected.id ? { ...a, ...form } : a))
      setSelected(null)
    } catch {
      setSaveMsg('error')
    } finally { setSaving(false) }
  }

  const filtered = React.useMemo(() => {
    let list = appointments
    if (statusFilter !== 'all') list = list.filter(a => a.status === statusFilter)
    if (search) {
      const q = search.toLowerCase()
      list = list.filter(a =>
        (a.name||'').toLowerCase().includes(q) ||
        (a.email||'').toLowerCase().includes(q) ||
        (a.service||'').toLowerCase().includes(q) ||
        (a.phone||'').includes(q)
      )
    }
    return list
  }, [appointments, statusFilter, search])

  const counts = React.useMemo(() => {
    const c = { all:appointments.length, pending:0, confirmed:0, completed:0, cancelled:0 }
    appointments.forEach(a => { if (c[a.status] !== undefined) c[a.status]++ })
    return c
  }, [appointments])

  // ── Real, computed dashboard metrics (no fabricated numbers) ──────────────────
  const todayISO = new Date().toISOString().slice(0,10)
  const newPhotoCount = React.useMemo(() => pending.reduce((n,p) => n + (p.newCount || 0), 0), [pending])

  const todaysAppts = React.useMemo(
    () => appointments.filter(a => dateOnly(a) === todayISO && a.status !== 'cancelled'),
    [appointments, todayISO]
  )
  const followUps = React.useMemo(
    () => appointments
      .filter(a => a.followUpDate && String(a.followUpDate).slice(0,10) >= todayISO && a.status !== 'cancelled')
      .sort((x,y) => String(x.followUpDate).slice(0,10).localeCompare(String(y.followUpDate).slice(0,10))),
    [appointments, todayISO]
  )
  const totalPatients = React.useMemo(
    () => new Set(appointments.map(a => (a.email || a.phone || a.id))).size,
    [appointments]
  )

  // Patients roster — one row per person (deduped by email/phone), aggregating all their visits
  const patients = React.useMemo(() => {
    const map = new Map()
    appointments.forEach(a => {
      const key = a.email || a.phone || a.id
      let p = map.get(key)
      if (!p) { p = { key, name:a.name||'Patient', email:a.email||'', phone:a.phone||'', visits:0, lastDate:'', lastVisit:'', service:'', diagnosis:'', diagDate:'', followUpDate:'', nextDate:'', latestAppt:a, appts:[] }; map.set(key, p) }
      p.visits++
      p.appts.push(a)
      const d = dateOnly(a)
      if (d && d >= p.lastDate) { p.lastDate = d; p.service = a.service || p.service; if (a.name) p.name = a.name; p.latestAppt = a }
      // "Last visit" is the last time they actually came, so a booking next week
      // does not overwrite it.
      if (d && d <= todayISO && d >= p.lastVisit && a.status !== 'cancelled') p.lastVisit = d
      if (a.diagnosis && d >= p.diagDate) { p.diagnosis = a.diagnosis; p.diagDate = d }
      const fu = a.followUpDate ? String(a.followUpDate).slice(0,10) : ''
      if (fu && fu >= todayISO && (!p.followUpDate || fu < p.followUpDate)) p.followUpDate = fu
      // Soonest appointment still ahead of us that is neither cancelled nor done.
      if (d && d >= todayISO && !['cancelled','completed'].includes(a.status)) {
        if (!p.nextDate || d < p.nextDate) p.nextDate = d
      }
    })
    return Array.from(map.values()).map(p => {
      p.appts.sort((x,y) => (dateOnly(y)||'').localeCompare(dateOnly(x)||''))
      // Three states the doctor acts on differently. "Follow Up" is the one that
      // matters: a follow-up is due and nothing has been booked for it yet.
      p.status = p.nextDate ? 'Ongoing' : p.followUpDate ? 'Follow Up' : 'Completed'
      return p
    }).sort((x,y) => (y.lastDate||'').localeCompare(x.lastDate||''))
  }, [appointments, todayISO])

  const filteredPatients = React.useMemo(() => {
    const q = search.toLowerCase()
    return patients.filter(p => {
      if (patientFilter !== 'All' && p.status !== patientFilter) return false
      if (!q) return true
      return (p.name||'').toLowerCase().includes(q) || (p.email||'').toLowerCase().includes(q) || (p.phone||'').includes(q)
    })
  }, [patients, search, patientFilter])

  const patientCounts = React.useMemo(() => {
    const c = { All: patients.length, Ongoing:0, 'Follow Up':0, Completed:0 }
    patients.forEach(p => { c[p.status] = (c[p.status]||0) + 1 })
    return c
  }, [patients])

  // Clinic alerts — real signals only. Rule-based flags (client) + AI-derived flags (server).
  const alerts = React.useMemo(() => {
    const out = []
    appointments.forEach(a => {
      // Overdue follow-up
      if (a.followUpDate && String(a.followUpDate).slice(0,10) < todayISO && !['completed','cancelled'].includes(a.status)) {
        const days = Math.floor((Date.now() - new Date(String(a.followUpDate).slice(0,10)).getTime()) / 86400000)
        out.push({ key:'fu-'+a.id, sev:'high', icon:'⏰', title:'Overdue follow-up', tag:'Follow-up needed',
          text:`Follow-up was due ${fmtDate(a.followUpDate)} (${days} day${days===1?'':'s'} ago) and is not yet completed.`,
          detected:String(a.followUpDate).slice(0,10), appt:a })
      }
      // Appointment awaiting confirmation for 2+ days
      if (a.status === 'pending' && a.createdAt) {
        const days = Math.floor((Date.now() - new Date(a.createdAt).getTime()) / 86400000)
        if (days >= 2) out.push({ key:'pc-'+a.id, sev:'med', icon:'📋', title:'Awaiting confirmation', tag:'Monitor',
          text:`Appointment has been awaiting confirmation for ${days} days.`, detected:String(a.createdAt).slice(0,10), appt:a })
      }
    })
    // New progress photos still to review
    pending.forEach(p => {
      const full = appointments.find(a => a.id === p.id)
      out.push({ key:'ph-'+p.id, sev:'med', icon:'🖼️', title:'New progress photos', tag:'Needs review',
        text:`${p.newCount} new progress photo${p.newCount===1?'':'s'} uploaded and awaiting your review.`,
        detected: dateOnly({ date:p.date }) || todayISO, appt: full, name: p.name })
    })
    // AI-derived flags from server (worse progress review, urgent/soon skin scan)
    aiAlerts.forEach((al, i) => {
      const full = appointments.find(a => a.id === al.appointmentId) || appointments.find(a => a.email && a.email === al.email)
      const isWorse = /worse/i.test(al.text || '')
      out.push({ key:'ai-'+i, sev: al.sev || 'high', icon: al.icon || '⚠️',
        title: isWorse ? 'Progress marked worse' : 'AI skin scan flag',
        tag: al.sev === 'high' ? 'Needs immediate attention' : 'Follow-up needed',
        text: al.text, detected: (al.createdAt || '').slice(0,10) || todayISO, appt: full, email: al.email, name: al.name || full?.name })
    })
    // Priority follows the patient's clinical condition when we have it (scan severity/urgency +
    // latest progress review); otherwise it keeps the alert's operational default.
    out.forEach(al => {
      const email = al.appt?.email || al.email
      const cond = email ? conditionByEmail[email] : null
      if (cond) { al.sev = cond; al.conditionBased = true }
    })
    const rank = { high:0, med:1, low:2 }
    return out.sort((x,y) => (rank[x.sev]||9) - (rank[y.sev]||9))
  }, [appointments, pending, aiAlerts, conditionByEmail, todayISO])
  const alertCounts = { high: alerts.filter(a=>a.sev==='high').length, med: alerts.filter(a=>a.sev==='med').length, low: alerts.filter(a=>a.sev==='low').length }
  const PRIORITY = { high:['High','#DC2626','#FEECEC'], med:['Medium','#EA580C','#FEEFE2'], low:['Low','#2563EB','#EAF1FE'] }
  const filteredAlerts = search ? alerts.filter(al => (al.appt?.name || al.name || '').toLowerCase().includes(search.toLowerCase())) : alerts

  // Featured timeline: most recent patient who has progress photos (else any with photos)
  const featured = React.useMemo(() => {
    const withProgress = appointments.find(a => parseArr(a.progressPhotos).length > 0)
    const withPhotos   = appointments.find(a => parseArr(a.photos).length > 0)
    return withProgress || withPhotos || appointments[0] || null
  }, [appointments])

  const timeline = React.useMemo(() => {
    if (!featured) return []
    const out = []
    const init = parseArr(featured.photos)
    if (init[0]) out.push({ label:'Initial', file:init[0], date: dateOnly(featured) })
    const prog = [...parseArr(featured.progressPhotos)].sort((a,b) => (parseInt(String(a).split('-')[0])||0) - (parseInt(String(b).split('-')[0])||0))
    prog.forEach((f, i) => {
      const ts = parseInt(String(f).split('-')[0])
      out.push({ label: i === prog.length-1 ? 'Latest' : `Update ${i+1}`, file:f, date: isNaN(ts) ? null : new Date(ts).toISOString().slice(0,10) })
    })
    return out.slice(-3)
  }, [featured])

  // Weekly bookings for last 6 weeks (real, from createdAt)
  const weeklyBookings = React.useMemo(() => {
    const weeks = []
    for (let i=5; i>=0; i--) {
      const end = new Date(); end.setHours(0,0,0,0); end.setDate(end.getDate() - i*7)
      const start = new Date(end); start.setDate(start.getDate() - 6)
      weeks.push({ start, end, label:`${start.getDate()}/${start.getMonth()+1}`, count:0 })
    }
    appointments.forEach(a => {
      const c = a.createdAt ? new Date(a.createdAt) : null
      if (!c || isNaN(c)) return
      weeks.forEach(w => { if (c >= w.start && c <= new Date(w.end.getTime() + 86399999)) w.count++ })
    })
    return weeks
  }, [appointments])
  const maxWeek = Math.max(1, ...weeklyBookings.map(w => w.count))

  const statusSegs = [
    ['Confirmed', counts.confirmed, '#16A34A'],
    ['Pending',   counts.pending,   '#D97706'],
    ['Completed', counts.completed, '#2563EB'],
    ['Cancelled', counts.cancelled, '#DC2626'],
  ]
  const segTotal = Math.max(1, statusSegs.reduce((s,x) => s + x[1], 0))

  // Patients/Appointments show the full list; Alerts shows the alerts page; else the overview
  const listView = activeNav === 'Patients' || activeNav === 'Appointments'
  const alertsView = activeNav === 'Alerts'
  const messagesView = activeNav === 'Messages'
  const unreadCount = messages.filter(m => !m.replyText).length
  const timeAgo = iso => {
    if (!iso) return ''
    const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000)
    if (mins < 1) return 'now'
    if (mins < 60) return `${mins}m`
    const h = Math.floor(mins/60); if (h < 24) return `${h}h`
    const d = Math.floor(h/24); if (d < 7) return `${d}d`
    return fmtDate(iso)
  }

  const scrollTo = id => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior:'smooth', block:'start' }) }
  const onNav = label => {
    setActiveNav(label)
    setDrawerOpen(false)
    if (label === 'Profile') { setProfileOpen(true); return }
    if (label === 'Label Data') { navigate('/label'); return }
    if (label === 'Medications') { navigate('/medication-training'); return }
    if (['Patients','Appointments','Alerts','Messages'].includes(label)) { window.scrollTo({ top:0, behavior:'smooth' }); return }
    scrollTo(NAV_TARGET[label] || 'dash-top')
  }
  const showList = () => { setActiveNav('Appointments'); window.scrollTo({ top:0, behavior:'smooth' }) }

  const greeting = (() => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening' })()
  const nowLabel = new Date().toLocaleDateString('en-IN', { day:'numeric', month:'long', year:'numeric' })

  const TILES = [
    { label:"Today's Appointments", value: todaysAppts.length,  icon:'📅', color:'#2563EB', bg:'#EAF1FE', go:() => { setStatusFilter('all'); showList() } },
    { label:'Follow-ups Due',       value: followUps.length,    icon:'👥', color:'#16A34A', bg:'#E7F7EE', go:() => scrollTo('followups') },
    { label:'New Progress Photos',  value: newPhotoCount,       icon:'🖼️', color:'#7C3AED', bg:'#F1EBFE', go:() => scrollTo('review-queue') },
    { label:'Alerts',               value: alerts.length,       icon:'⚠️', color:'#EA580C', bg:'#FEEFE2', go:() => { setActiveNav('Alerts'); window.scrollTo({ top:0, behavior:'smooth' }) } },
    { label:'Completed Consults',   value: counts.completed,    icon:'✅', color:'#0891B2', bg:'#E3F6FA', go:() => { setStatusFilter('completed'); showList() } },
    { label:'Total Patients',       value: totalPatients,       icon:'🧑‍🤝‍🧑', color:'#D97706', bg:'#FEF3E2', go:() => { setActiveNav('Patients'); window.scrollTo({ top:0, behavior:'smooth' }) } },
  ]

  if (!user?.token) return null

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:U.bg, fontFamily:"'DM Sans',sans-serif", color:U.text }}>

      {settingsOpen && (
        <DoctorSettings user={user} apiBase={API_BASE} fetchWithAuth={fetchWithAuth} updateUser={updateUser} logout={logout} onClose={() => setSettingsOpen(false)} />
      )}
      {profileOpen && (
        <ProfileModal apiBase={API_BASE} fetchWithAuth={fetchWithAuth} user={user} updateUser={updateUser} onClose={() => setProfileOpen(false)} />
      )}

      {/* Mobile drawer backdrop */}
      {isMobile && drawerOpen && (
        <div onClick={() => setDrawerOpen(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.4)', zIndex:1100 }} />
      )}

      {/* ── SIDEBAR (fixed off-canvas drawer on mobile) ──────────────────────── */}
      <aside style={ isMobile
        ? { width:240, background:U.card, borderRight:`1px solid ${U.border}`, position:'fixed', top:0, left:0, height:'100vh', display:'flex', flexDirection:'column', padding:'1.25rem 0', zIndex:1200, transform: drawerOpen ? 'translateX(0)' : 'translateX(-100%)', transition:'transform .25s ease', boxShadow: drawerOpen ? '0 0 40px rgba(0,0,0,.25)' : 'none' }
        : { width:220, flexShrink:0, background:U.card, borderRight:`1px solid ${U.border}`, position:'sticky', top:0, height:'100vh', display:'flex', flexDirection:'column', padding:'1.25rem 0' } }>
        <button onClick={() => { setActiveNav('Profile'); setProfileOpen(true); setDrawerOpen(false) }} title="Edit profile"
          style={{ padding:'.9rem 1rem', margin:'0 .75rem 1rem', background:U.bg, border:`1px solid ${U.border}`, borderRadius:12, display:'flex', alignItems:'center', gap:10, width:'calc(100% - 1.5rem)', cursor:'pointer', fontFamily:'inherit', textAlign:'left' }}>
          {docPhoto
            ? <img src={photoUrl(docPhoto)} alt="" style={{ width:38, height:38, borderRadius:'50%', objectFit:'cover', flexShrink:0, background:'#eee' }} />
            : <div style={{ width:38, height:38, borderRadius:'50%', background:`linear-gradient(135deg,${C.g700},${C.g500})`, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontFamily:"'DM Serif Display',serif", fontSize:'1.05rem', flexShrink:0 }}>
              {(user?.name || 'D')[0].toUpperCase()}
            </div>}
          <div style={{ minWidth:0, flex:1 }}>
            <div style={{ fontSize:'.82rem', fontWeight:700, color:U.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{docName(user?.name)}</div>
            <div style={{ fontSize:'.68rem', color:U.muted }}>Dermatology · Edit ✎</div>
          </div>
        </button>

        <nav style={{ display:'flex', flexDirection:'column', gap:2, padding:'0 .75rem', flex:1 }}>
          {NAV.map(n => {
            const on = activeNav === n.label
            return (
              <button key={n.label} onClick={() => onNav(n.label)}
                style={{ display:'flex', alignItems:'center', gap:11, padding:'.65rem .8rem', borderRadius:10, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:'.86rem', fontWeight: on ? 700 : 500, textAlign:'left', background: on ? U.primarySoft : 'transparent', color: on ? U.primary : U.muted, transition:'all .15s' }}
                onMouseEnter={e => { if (!on) e.currentTarget.style.background = U.bg }}
                onMouseLeave={e => { if (!on) e.currentTarget.style.background = 'transparent' }}>
                <span style={{ fontSize:'1rem' }}>{n.icon}</span>
                <span style={{ flex:1 }}>{n.label}</span>
                {n.label === 'Alerts' && alerts.length > 0 && (
                  <span style={{ fontSize:'.66rem', fontWeight:700, color:'#fff', background:'#EA580C', borderRadius:999, padding:'.05rem .4rem', minWidth:18, textAlign:'center' }}>{alerts.length}</span>
                )}
                {n.label === 'Messages' && unreadCount > 0 && (
                  <span style={{ fontSize:'.66rem', fontWeight:700, color:'#fff', background:U.primary, borderRadius:999, padding:'.05rem .4rem', minWidth:18, textAlign:'center' }}>{unreadCount}</span>
                )}
              </button>
            )
          })}
        </nav>

        <div style={{ padding:'0 .75rem' }}>
          <button onClick={load} style={{ width:'100%', padding:'.6rem', borderRadius:10, background:U.bg, border:`1px solid ${U.border}`, color:U.muted, cursor:'pointer', fontFamily:'inherit', fontSize:'.8rem', fontWeight:600 }}>🔄 Refresh</button>
        </div>
      </aside>

      {/* ── MAIN ────────────────────────────────────────────────────────────── */}
      <main style={{ flex:1, minWidth:0, padding: isMobile ? '0 1rem 5.5rem' : '1.75rem 2rem 3rem' }}>

        {/* Mobile branded top bar (Revive Doctor) */}
        {isMobile && (<>
          <div style={{ position:'sticky', top:0, zIndex:900, background:'#fff', margin:'0 -1rem', padding:'.6rem 1rem', display:'flex', alignItems:'center', gap:10, borderBottom:`1px solid ${U.border}` }}>
            <button onClick={() => setSettingsOpen(true)} aria-label="Open menu"
              style={{ width:42, height:42, borderRadius:12, border:`1px solid ${U.border}`, background:'#fff', cursor:'pointer', fontSize:'1.15rem', flexShrink:0 }}>☰</button>
            <div style={{ flex:1, display:'flex', alignItems:'center', gap:7 }}>
              <svg width="27" height="27" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 20.5S3.5 15.5 2 9.8C1.1 6.4 3.2 4 6 4c1.9 0 3.1 1.1 4 2.2C10.9 5.1 12.1 4 14 4c2.8 0 4.9 2.4 4 5.8C20.5 15.5 12 20.5 12 20.5z" fill="#14B8A6"/>
                <path d="M12 8.2v5M9.5 10.7h5" stroke="#fff" strokeWidth="1.7" strokeLinecap="round"/>
              </svg>
              <div style={{ lineHeight:1.02 }}>
                <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.08rem', color:'#14B8A6', fontWeight:700 }}>Revive</div>
                <div style={{ fontSize:'.72rem', color:'#1E3A5F', fontWeight:800 }}>Doctor</div>
              </div>
            </div>
            <button onClick={() => { setActiveNav('Alerts'); window.scrollTo({ top:0, behavior:'smooth' }) }} aria-label="Alerts"
              style={{ position:'relative', width:42, height:42, borderRadius:12, border:`1px solid ${U.border}`, background:'#fff', cursor:'pointer', fontSize:'1.1rem', flexShrink:0 }}>🔔
              {alerts.length > 0 && <span style={{ position:'absolute', top:-4, right:-4, fontSize:'.58rem', fontWeight:700, color:'#fff', background:'#EF4444', borderRadius:999, padding:'.05rem .32rem' }}>{alerts.length}</span>}
            </button>
          </div>

          {/* Dashboard greeting (overview only) */}
          {!listView && !alertsView && !messagesView && (
            <div id="dash-top" style={{ display:'flex', alignItems:'center', gap:13, padding:'1.15rem .15rem .35rem' }}>
              {docPhoto
                ? <img src={photoUrl(docPhoto)} alt="" style={{ width:54, height:54, borderRadius:'50%', objectFit:'cover', flexShrink:0, background:'#eee' }} />
                : <div style={{ width:54, height:54, borderRadius:'50%', background:`linear-gradient(135deg,${U.primary},#60A5FA)`, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontFamily:"'DM Serif Display',serif", fontSize:'1.4rem', flexShrink:0 }}>
                  {(user?.name || 'D')[0].toUpperCase()}
                </div>}
              <div style={{ minWidth:0 }}>
                <div style={{ fontSize:'.88rem', color:U.muted }}>{greeting},</div>
                <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.3rem', color:U.text, lineHeight:1.12 }}>{docName(user?.name)} 👋</div>
                <div style={{ fontSize:'.8rem', color:U.muted, marginTop:2 }}>Here's what needs your attention today.</div>
              </div>
            </div>
          )}
        </>)}

        {/* Top bar (desktop) */}
        {!isMobile && (
        <div id="dash-top" style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'1rem', flexWrap:'wrap', marginBottom:'1.5rem' }}>
          <div>
            <h1 style={{ fontFamily:"'DM Serif Display',serif", fontSize:'clamp(1.35rem,2.4vw,1.9rem)', color:U.text, margin:0 }}>
              {alertsView ? 'AI Alerts' : messagesView ? 'Messages' : `${greeting}, ${docName(user?.name)} 👋`}
            </h1>
            <p style={{ fontSize:'.85rem', color:U.muted, margin:'.25rem 0 0' }}>
              {alertsView ? 'Real signals from your patient data that may need attention.'
                : messagesView ? 'Check-ins and notes your patients have written.'
                : "Here's what's happening in your clinic today."}
            </p>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'.6rem' }}>
            <div style={{ position:'relative' }}>
              <span style={{ position:'absolute', left:'.8rem', top:'50%', transform:'translateY(-50%)', color:U.muted, fontSize:'.85rem' }}>🔍</span>
              <input value={search} onChange={e => { setSearch(e.target.value); if (!listView && !alertsView) setActiveNav('Appointments') }} placeholder="Search patients, alerts…"
                style={{ width:280, maxWidth:'50vw', padding:'.6rem .9rem .6rem 2.3rem', border:`1px solid ${U.border}`, borderRadius:10, fontSize:'.82rem', outline:'none', fontFamily:'inherit', background:U.card, boxSizing:'border-box', color:U.text }} />
            </div>
            <div style={{ padding:'.55rem .9rem', border:`1px solid ${U.border}`, borderRadius:10, background:U.card, fontSize:'.8rem', color:U.muted, whiteSpace:'nowrap' }}>📅 {nowLabel}</div>
          </div>
        </div>
        )}

        {(!listView && !messagesView) && <DoctorEscalations />}

        {/* ── MOBILE DASHBOARD (mockup) ─────────────────────────────────────── */}
        {!listView && !alertsView && !messagesView && isMobile && (<>
          {/* 2x2 stat cards */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'.7rem', margin:'1rem 0 1.2rem' }}>
            {TILES.slice(0,4).map(t => (
              <button key={t.label} onClick={t.go}
                style={{ position:'relative', textAlign:'left', background:U.card, border:`1px solid ${U.border}`, borderRadius:16, padding:'1rem', cursor:'pointer', fontFamily:'inherit', boxShadow:'0 1px 2px rgba(16,24,40,.04)' }}>
                <div style={{ width:40, height:40, borderRadius:11, background:t.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.15rem', marginBottom:'.7rem' }}>{t.icon}</div>
                <div style={{ fontSize:'.76rem', color:U.muted, fontWeight:600, lineHeight:1.25, paddingRight:12 }}>{t.label}</div>
                <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.7rem', color:U.text, lineHeight:1.1, marginTop:2 }}>{loading ? '—' : t.value}</div>
                <span style={{ position:'absolute', top:'1rem', right:'.9rem', color:U.muted, fontSize:'1rem' }}>›</span>
              </button>
            ))}
          </div>

          {/* AI Assistant — assisted reminder queue (only appears when enabled server-side) */}
          {agentTasks?.enabled && (
            <div style={{ background:U.card, border:`1px solid ${U.border}`, borderRadius:16, padding:'.95rem', marginBottom:'1.1rem' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'.55rem' }}>
                <div style={{ display:'flex', alignItems:'center', gap:7, fontWeight:700, color:U.text, fontSize:'.92rem' }}><span>🤖</span> AI Assistant</div>
                <span style={{ fontSize:'.62rem', fontWeight:700, color:U.primary, background:'#EAF1FE', borderRadius:999, padding:'.2rem .55rem', letterSpacing:'.03em' }}>PREVIEW</span>
              </div>
              <div style={{ fontSize:'.77rem', color:U.muted, marginBottom:'.4rem', lineHeight:1.4 }}>
                {agentTasks.tasks.length
                  ? `I've prepared ${agentTasks.tasks.length} reminder${agentTasks.tasks.length > 1 ? 's' : ''} for your approval. Nothing is sent until you approve.`
                  : 'No reminders waiting. Scan your upcoming appointments to prepare some.'}
              </div>
              {agentTasks.tasks.map(t => (
                <div key={t.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'.6rem 0', borderTop:`1px solid ${U.border}` }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:'.82rem', fontWeight:600, color:U.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.label}</div>
                    <div style={{ fontSize:'.68rem', color:U.muted }}>Send appointment reminder</div>
                  </div>
                  <button disabled={agentBusy} onClick={() => agentAct(t.id, 'approve')} style={{ padding:'.4rem .75rem', borderRadius:9, border:'none', background:'#16A34A', color:'#fff', fontSize:'.75rem', fontWeight:700, cursor:'pointer', fontFamily:'inherit', flexShrink:0 }}>Approve</button>
                  <button disabled={agentBusy} onClick={() => agentAct(t.id, 'reject')} title="Dismiss" style={{ padding:'.4rem .6rem', borderRadius:9, border:`1px solid ${U.border}`, background:'#fff', color:U.muted, fontSize:'.8rem', fontWeight:700, cursor:'pointer', fontFamily:'inherit', flexShrink:0 }}>✕</button>
                </div>
              ))}
              <button disabled={agentBusy} onClick={agentScan} style={{ marginTop:'.7rem', width:'100%', padding:'.6rem', borderRadius:10, border:`1px dashed ${U.primary}`, background:'#F5F9FF', color:U.primary, fontSize:'.8rem', fontWeight:700, cursor:agentBusy ? 'default' : 'pointer', fontFamily:'inherit', opacity:agentBusy ? .6 : 1 }}>
                {agentBusy ? 'Working…' : '🔍 Scan upcoming appointments'}
              </button>
            </div>
          )}

          {/* Needs Your Attention */}
          {alerts.length > 0 && (
            <div style={{ background:'#FEF4F4', border:'1px solid #FBD5D5', borderRadius:16, padding:'.85rem', marginBottom:'1.1rem' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'.6rem' }}>
                <div style={{ display:'flex', alignItems:'center', gap:7, fontWeight:700, color:U.text, fontSize:'.92rem' }}><span>⚠️</span> Needs Your Attention</div>
                <button onClick={() => { setActiveNav('Alerts'); window.scrollTo({ top:0, behavior:'smooth' }) }} style={{ background:'none', border:'none', color:U.primary, fontSize:'.78rem', fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>View all →</button>
              </div>
              <div style={{ background:U.card, borderRadius:12, overflow:'hidden' }}>
                {alerts.slice(0,3).map((al, i) => (
                  <div key={al.key} style={{ display:'flex', alignItems:'center', gap:9, padding:'.7rem .75rem', borderTop: i ? `1px solid ${U.border}` : 'none' }}>
                    <span style={{ width:9, height:9, borderRadius:'50%', flexShrink:0, background: al.sev==='high' ? '#DC2626' : '#EA580C' }} />
                    <div style={{ width:36, height:36, borderRadius:'50%', background:`linear-gradient(135deg,${U.primary},#60A5FA)`, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontFamily:"'DM Serif Display',serif", fontSize:'.9rem', flexShrink:0 }}>{(al.appt?.name || al.name || 'P')[0].toUpperCase()}</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:'.85rem', fontWeight:700, color:U.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{al.appt?.name || al.name || 'Patient'}</div>
                      <div style={{ fontSize:'.73rem', color: al.sev==='high' ? '#DC2626' : U.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{al.text}</div>
                    </div>
                    {al.appt && <button onClick={() => open(al.appt)} style={{ padding:'.4rem .75rem', borderRadius:9, border:`1px solid ${U.primary}`, background:'#fff', color:U.primary, fontSize:'.75rem', fontWeight:700, cursor:'pointer', fontFamily:'inherit', flexShrink:0 }}>Review</button>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Patient Activity */}
          <div style={{ background:U.card, border:`1px solid ${U.border}`, borderRadius:16, marginBottom:'1rem', overflow:'hidden' }}>
            <div style={{ fontWeight:700, color:U.text, fontSize:'.92rem', padding:'.85rem .9rem .5rem' }}>Recent Patient Activity</div>
            {[
              ['🖼️','#F1EBFE','#7C3AED', `${newPhotoCount} new progress photos`, 'Patients uploaded new photos', newPhotoCount, () => { if (pending[0]) open(pending[0]) }],
              ['🔔','#FEEFE2','#EA580C', `${alerts.length} clinical alerts`, 'Signals needing your review', alerts.length, () => { setActiveNav('Alerts'); window.scrollTo({ top:0, behavior:'smooth' }) }],
              ['💬','#E7F7EE','#16A34A', `${unreadCount} new messages`, 'Unread patient messages', unreadCount, () => { setActiveNav('Messages'); window.scrollTo({ top:0, behavior:'smooth' }) }],
            ].map(([icon,bg,cc,title,sub,count,go], i) => (
              <button key={i} onClick={go} style={{ width:'100%', display:'flex', alignItems:'center', gap:11, padding:'.7rem .9rem', background:'none', border:'none', borderTop:`1px solid ${U.border}`, cursor:'pointer', fontFamily:'inherit', textAlign:'left' }}>
                <div style={{ width:40, height:40, borderRadius:11, background:bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.05rem', flexShrink:0 }}>{icon}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:'.85rem', fontWeight:700, color:U.text }}>{title}</div>
                  <div style={{ fontSize:'.73rem', color:U.muted }}>{sub}</div>
                </div>
                <span style={{ minWidth:26, height:26, padding:'0 .4rem', borderRadius:8, background:bg, color:cc, fontSize:'.8rem', fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{count}</span>
                <span style={{ color:U.muted }}>›</span>
              </button>
            ))}
          </div>
        </>)}

        {/* ── DESKTOP OVERVIEW ──────────────────────────────────────────────── */}
        {!listView && !alertsView && !messagesView && !isMobile && (<>
        {/* Stat tiles */}
        <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit,minmax(180px,1fr))', gap: isMobile ? '.7rem' : '1rem', marginBottom:'1.5rem' }}>
          {TILES.map(t => (
            <button key={t.label} onClick={t.go}
              style={{ textAlign:'left', background:U.card, border:`1px solid ${U.border}`, borderRadius:16, padding:'1.1rem 1.25rem', cursor:'pointer', fontFamily:'inherit', boxShadow:'0 1px 2px rgba(16,24,40,.04)', transition:'transform .12s, box-shadow .12s' }}
              onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 8px 20px rgba(16,24,40,.08)' }}
              onMouseLeave={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='0 1px 2px rgba(16,24,40,.04)' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:'.6rem' }}>
                <div style={{ width:38, height:38, borderRadius:10, background:t.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.05rem' }}>{t.icon}</div>
                <div style={{ fontSize:'.78rem', color:U.muted, fontWeight:600 }}>{t.label}</div>
              </div>
              <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.9rem', color:U.text, lineHeight:1 }}>{loading ? '—' : t.value}</div>
            </button>
          ))}
        </div>

        {/* Timeline + right rail */}
        <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,2fr) minmax(260px,1fr)', gap:'1.25rem', marginBottom:'1.5rem' }}>

          {/* Patient timeline (real photos + doctor's own diagnosis) */}
          <Card title="Patient Timeline"
            action={featured && <button onClick={() => open(featured)} style={{ fontSize:'.78rem', color:U.primary, background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', fontWeight:600 }}>Open chart →</button>}>
            {!featured ? (
              <div style={{ color:U.muted, fontSize:'.85rem' }}>No patient photos yet.</div>
            ) : (
              <>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:'1rem' }}>
                  <div style={{ width:42, height:42, borderRadius:'50%', background:`linear-gradient(135deg,${C.g700},${C.g500})`, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontFamily:"'DM Serif Display',serif", fontSize:'1.1rem' }}>{(featured.name||'?')[0].toUpperCase()}</div>
                  <div>
                    <div style={{ fontWeight:700, color:U.text, fontSize:'.95rem' }}>{featured.name || '—'}</div>
                    <div style={{ fontSize:'.75rem', color:U.muted }}>{featured.service || 'Consultation'}{featured.diagnosis ? ` · ${featured.diagnosis.split(/[\s,]+/)[0]}` : ''}</div>
                  </div>
                </div>

                {timeline.length > 0 ? (
                  <div style={{ display:'flex', gap:12, marginBottom:'1rem', flexWrap:'wrap' }}>
                    {timeline.map((t, i) => (
                      <a key={i} href={photoUrl(t.file)} target="_blank" rel="noopener noreferrer" style={{ flex:'1 1 120px', minWidth:110, textDecoration:'none' }}>
                        <div style={{ width:'100%', height:150, borderRadius:12, overflow:'hidden', border:`1px solid ${U.border}`, background:U.bg }}>
                          <img src={photoUrl(t.file)} alt={t.label} style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e => { e.target.style.display='none' }} />
                        </div>
                        <div style={{ marginTop:6 }}>
                          <div style={{ fontSize:'.75rem', fontWeight:700, color:U.text }}>{t.label}</div>
                          <div style={{ fontSize:'.68rem', color:U.muted }}>{t.date ? fmtDate(t.date) : ''}</div>
                        </div>
                      </a>
                    ))}
                  </div>
                ) : (
                  <div style={{ color:U.muted, fontSize:'.82rem', marginBottom:'1rem' }}>No progress photos uploaded yet.</div>
                )}

                <div style={{ background:U.bg, borderRadius:12, padding:'.9rem 1rem' }}>
                  <div style={{ fontSize:'.68rem', fontWeight:700, color:U.muted, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'.35rem' }}>Latest diagnosis (your notes)</div>
                  <div style={{ fontSize:'.83rem', color:U.text, lineHeight:1.6, whiteSpace:'pre-wrap' }}>{featured.diagnosis || 'No diagnosis recorded yet — open the chart to add one.'}</div>
                  {featured.followUpDate && <div style={{ fontSize:'.75rem', color:U.primary, marginTop:'.5rem', fontWeight:600 }}>📅 Follow-up: {fmtDate(featured.followUpDate)}</div>}
                </div>
              </>
            )}
          </Card>

          {/* Right rail: alerts + follow-ups + review queue */}
          <div style={{ display:'flex', flexDirection:'column', gap:'1.25rem' }}>
            <Card title="Alerts" id="alerts-card" pad={false}
              action={<span style={{ fontSize:'.66rem', color:U.muted }}>from your patient data</span>}>
              {alerts.length === 0 ? (
                <div style={{ padding:'1rem 1.25rem', color:U.muted, fontSize:'.82rem' }}>✓ No alerts — everything looks on track.</div>
              ) : (
                <div>
                  {alerts.slice(0,8).map((al, i) => (
                    <button key={al.key} onClick={() => al.appt && open(al.appt)}
                      style={{ width:'100%', textAlign:'left', display:'flex', alignItems:'flex-start', gap:10, padding:'.7rem 1.25rem', background:'none', border:'none', borderTop: i ? `1px solid ${U.border}` : 'none', cursor: al.appt ? 'pointer' : 'default', fontFamily:'inherit' }}>
                      <span style={{ width:8, height:8, borderRadius:'50%', marginTop:6, flexShrink:0, background: al.sev==='high' ? '#DC2626' : '#EA580C' }} />
                      <div style={{ minWidth:0 }}>
                        <div style={{ fontSize:'.82rem', fontWeight:600, color:U.text }}>{al.appt?.name || al.name || 'Patient'}</div>
                        <div style={{ fontSize:'.72rem', color:U.muted }}>{al.icon} {al.text}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </Card>

            <Card title="Upcoming Follow-ups" id="followups" pad={false}>
              {followUps.length === 0 ? (
                <div style={{ padding:'1rem 1.25rem', color:U.muted, fontSize:'.82rem' }}>No follow-ups scheduled.</div>
              ) : (
                <div>
                  {followUps.slice(0,6).map((a, i) => (
                    <button key={a.id} onClick={() => open(a)}
                      style={{ width:'100%', textAlign:'left', display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, padding:'.7rem 1.25rem', background:'none', border:'none', borderTop: i ? `1px solid ${U.border}` : 'none', cursor:'pointer', fontFamily:'inherit' }}>
                      <div style={{ minWidth:0 }}>
                        <div style={{ fontSize:'.82rem', fontWeight:600, color:U.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.name || 'Patient'}</div>
                        <div style={{ fontSize:'.7rem', color:U.muted }}>{a.diagnosis ? a.diagnosis.split(/[\s,]+/)[0] : (a.service || 'Consultation')}</div>
                      </div>
                      <span style={{ fontSize:'.72rem', color:U.primary, fontWeight:600, whiteSpace:'nowrap' }}>{fmtDate(a.followUpDate)}</span>
                    </button>
                  ))}
                </div>
              )}
            </Card>

            <Card title="Photos to Review" id="review-queue" pad={false}
              action={newPhotoCount > 0 && <span style={{ fontSize:'.66rem', color:'#fff', background:'#DC2626', padding:'.15rem .5rem', borderRadius:999, fontWeight:700 }}>{newPhotoCount} new</span>}>
              {pending.length === 0 ? (
                <div style={{ padding:'1rem 1.25rem', color:U.muted, fontSize:'.82rem' }}>All caught up — nothing to review.</div>
              ) : (
                <div>
                  {pending.map((p, i) => {
                    const full = appointments.find(a => a.id === p.id)
                    return (
                      <button key={p.id} onClick={() => full && open(full)}
                        style={{ width:'100%', textAlign:'left', display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, padding:'.7rem 1.25rem', background:'none', border:'none', borderTop: i ? `1px solid ${U.border}` : 'none', cursor:'pointer', fontFamily:'inherit' }}>
                        <div style={{ minWidth:0 }}>
                          <div style={{ fontSize:'.82rem', fontWeight:600, color:U.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.name || 'Patient'}</div>
                          <div style={{ fontSize:'.7rem', color:U.muted }}>{p.service || 'Consultation'} · {fmtDate(p.date)}</div>
                        </div>
                        <span style={{ fontSize:'.66rem', color:'#fff', background:'#DC2626', padding:'.15rem .5rem', borderRadius:999, fontWeight:700, whiteSpace:'nowrap' }}>{p.newCount} new</span>
                      </button>
                    )
                  })}
                </div>
              )}
            </Card>
          </div>
        </div>

        {/* Analytics (all real) */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:'1.25rem', marginBottom:'1.5rem' }}>
          <Card title="Appointments by Status">
            <div style={{ display:'flex', height:14, borderRadius:999, overflow:'hidden', background:U.bg, marginBottom:'1rem' }}>
              {statusSegs.map(([label,val,color]) => val > 0 && (
                <div key={label} title={`${label}: ${val}`} style={{ width:`${(val/segTotal)*100}%`, background:color }} />
              ))}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'.5rem' }}>
              {statusSegs.map(([label,val,color]) => (
                <div key={label} style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ width:10, height:10, borderRadius:3, background:color, flexShrink:0 }} />
                  <span style={{ fontSize:'.8rem', color:U.muted }}>{label}</span>
                  <span style={{ fontSize:'.8rem', fontWeight:700, color:U.text, marginLeft:'auto' }}>{val}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card title="New Bookings — last 6 weeks">
            <div style={{ display:'flex', alignItems:'flex-end', gap:10, height:120, padding:'0 .25rem' }}>
              {weeklyBookings.map((w, i) => (
                <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
                  <div style={{ fontSize:'.7rem', fontWeight:700, color:U.text }}>{w.count}</div>
                  <div style={{ width:'100%', maxWidth:34, height:`${Math.max(4,(w.count/maxWeek)*90)}px`, background: i === weeklyBookings.length-1 ? U.primary : '#BFD3F5', borderRadius:'6px 6px 0 0', transition:'height .3s' }} />
                  <div style={{ fontSize:'.66rem', color:U.muted }}>{w.label}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Quick actions */}
        <Card title="Quick actions">
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(110px,1fr))', gap:'.75rem' }}>
            {[
              ['New Consultation', '➕', () => navigate('/book-appointment')],
              ['Review Photos',    '🖼️', () => { setActiveNav('Dashboard'); scrollTo('review-queue') }],
              ['Patients',         '👥', () => { setActiveNav('Patients'); window.scrollTo({ top:0, behavior:'smooth' }) }],
              ['View Alerts',      '⚠️', () => { setActiveNav('Alerts'); window.scrollTo({ top:0, behavior:'smooth' }) }],
              ['Label Data',       '🏷️', () => navigate('/label')],
            ].map(([label, icon, act]) => (
              <button key={label} onClick={act}
                style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:8, padding:'1rem .5rem', borderRadius:12, border:`1px solid ${U.border}`, background:U.card, cursor:'pointer', fontFamily:'inherit', transition:'background .15s' }}
                onMouseEnter={e => e.currentTarget.style.background=U.bg}
                onMouseLeave={e => e.currentTarget.style.background=U.card}>
                <div style={{ width:42, height:42, borderRadius:12, background:U.primarySoft, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.2rem' }}>{icon}</div>
                <span style={{ fontSize:'.74rem', fontWeight:600, color:U.text, textAlign:'center', lineHeight:1.3 }}>{label}</span>
              </button>
            ))}
          </div>
        </Card>
        </>)}

        {/* ── MOBILE LIST (Appointments / Patients) ─────────────────────────── */}
        {listView && isMobile && (<>
          <div style={{ padding:'1.1rem .15rem .35rem' }}>
            <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.5rem', color:U.text }}>{activeNav === 'Patients' ? 'Patients' : 'Appointments'}</div>
            <div style={{ fontSize:'.82rem', color:U.muted, marginTop:2 }}>{activeNav === 'Patients' ? 'Manage and view your patients' : 'View and manage your appointments'}</div>
          </div>

          <div style={{ position:'relative', margin:'.7rem 0 .9rem' }}>
            <span style={{ position:'absolute', left:'.85rem', top:'50%', transform:'translateY(-50%)', color:U.muted, fontSize:'.85rem' }}>🔍</span>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={activeNav === 'Patients' ? 'Search by name, phone…' : 'Search appointments…'}
              style={{ width:'100%', padding:'.7rem .9rem .7rem 2.3rem', border:`1px solid ${U.border}`, borderRadius:12, fontSize:'.85rem', outline:'none', fontFamily:'inherit', background:U.card, boxSizing:'border-box', color:U.text }} />
          </div>

          {activeNav === 'Patients' ? (
            filteredPatients.length === 0 ? (
              <div style={{ textAlign:'center', padding:'3rem', color:U.muted }}>No patients found</div>
            ) : filteredPatients.map(p => (
              <button key={p.key} onClick={() => p.email ? setPatientModal(p.email) : (p.latestAppt && open(p.latestAppt))}
                style={{ width:'100%', textAlign:'left', display:'flex', alignItems:'center', gap:11, background:U.card, border:`1px solid ${U.border}`, borderRadius:16, padding:'.85rem', marginBottom:'.7rem', cursor:'pointer', fontFamily:'inherit' }}>
                <div style={{ width:46, height:46, borderRadius:'50%', background:`linear-gradient(135deg,${U.primary},#60A5FA)`, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontFamily:"'DM Serif Display',serif", fontSize:'1.1rem', flexShrink:0 }}>{(p.name||'?')[0].toUpperCase()}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, color:U.text, fontSize:'.92rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.name||'—'}</div>
                  <div style={{ fontSize:'.75rem', color:U.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>📞 {p.phone || p.email || '—'}</div>
                  <div style={{ display:'flex', gap:6, marginTop:5, flexWrap:'wrap' }}>
                    <span style={{ fontSize:'.68rem', color:U.primary, background:U.primarySoft, padding:'.15rem .5rem', borderRadius:999 }}>{p.visits} visit{p.visits===1?'':'s'}</span>
                    {p.diagnosis && <span style={{ fontSize:'.68rem', color:U.muted, background:U.bg, padding:'.15rem .5rem', borderRadius:999 }}>{p.diagnosis.split(/[\s,]+/)[0]}</span>}
                    {p.followUpDate && <span style={{ fontSize:'.68rem', color:'#D97706', background:'#FEF3E2', padding:'.15rem .5rem', borderRadius:999 }}>Follow-up {fmtDate(p.followUpDate)}</span>}
                  </div>
                </div>
                <span style={{ color:U.muted, flexShrink:0 }}>›</span>
              </button>
            ))
          ) : (<>
            {/* Add appointment */}
            <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:'.7rem' }}>
              <button onClick={() => navigate('/book-appointment')}
                style={{ display:'flex', alignItems:'center', gap:6, padding:'.6rem 1rem', borderRadius:11, background:U.primary, color:'#fff', border:'none', fontSize:'.82rem', fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>+ Add Appointment</button>
            </div>

            {/* Day selector */}
            <div style={{ display:'flex', alignItems:'center', gap:4, background:U.card, border:`1px solid ${U.border}`, borderRadius:14, padding:'.4rem', marginBottom:'1rem' }}>
              <button onClick={() => setWeekStart(addDaysISO(weekStart, -7))} style={{ background:'none', border:'none', color:U.muted, fontSize:'1.15rem', cursor:'pointer', padding:'.2rem .25rem', flexShrink:0 }}>‹</button>
              <div style={{ display:'flex', flex:1, gap:3 }}>
                {[0,1,2,3,4].map(i => {
                  const d = addDaysISO(weekStart, i); const on = d === selDay
                  return (
                    <button key={d} onClick={() => setSelDay(d)}
                      style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:2, padding:'.5rem .1rem', borderRadius:10, border:'none', cursor:'pointer', fontFamily:'inherit', background: on ? U.primarySoft : 'transparent' }}>
                      <span style={{ fontSize:'.66rem', fontWeight:600, color: on ? U.primary : U.muted }}>{weekdayShort(d)}</span>
                      <span style={{ fontSize:'.74rem', fontWeight:700, color: on ? U.primary : U.text }}>{dayMonShort(d)}</span>
                    </button>
                  )
                })}
              </div>
              <button onClick={() => setWeekStart(addDaysISO(weekStart, 7))} style={{ background:'none', border:'none', color:U.muted, fontSize:'1.15rem', cursor:'pointer', padding:'.2rem .25rem', flexShrink:0 }}>›</button>
            </div>

            {(() => {
              const dayAppts = appointments.filter(a => dateOnly(a) === selDay && (!search || (a.name||'').toLowerCase().includes(search.toLowerCase())))
              const dCount = st => dayAppts.filter(a => st.includes(a.status)).length
              const cards = [
                ['Total Appointments','📅','#EAF1FE','#2563EB', dayAppts.length],
                ['Completed','✅','#E7F7EE','#16A34A', dCount(['completed'])],
                ['Upcoming','🕐','#FEF3E2','#D97706', dCount(['pending','confirmed'])],
                ['Cancelled','⊘','#F3EEFE','#7C3AED', dCount(['cancelled'])],
              ]
              const cnt = {}; appointments.forEach(a => { const k = a.email || a.phone; if (k) cnt[k] = (cnt[k]||0)+1 })
              const groups = [
                ['Morning','9:00 AM – 12:00 PM', dayAppts.filter(a => { const h = apptHour(a); return h===null || (h>=5 && h<12) })],
                ['Afternoon','12:00 PM – 4:00 PM', dayAppts.filter(a => { const h = apptHour(a); return h!==null && h>=12 && h<16 })],
                ['Evening','4:00 PM – 8:00 PM', dayAppts.filter(a => { const h = apptHour(a); return h!==null && h>=16 })],
              ]
              return (<>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'.6rem', marginBottom:'1.1rem' }}>
                  {cards.map(([lbl,icon,bg,cc,val]) => (
                    <div key={lbl} style={{ background:U.card, border:`1px solid ${U.border}`, borderRadius:14, padding:'.8rem .85rem' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:'.35rem' }}>
                        <div style={{ width:30, height:30, borderRadius:9, background:bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'.9rem' }}>{icon}</div>
                        <div style={{ fontSize:'.71rem', color:U.muted, fontWeight:600, lineHeight:1.15 }}>{lbl}</div>
                      </div>
                      <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.5rem', color:cc, lineHeight:1 }}>{loading ? '—' : val}</div>
                    </div>
                  ))}
                </div>

                {dayAppts.length === 0 ? (
                  <div style={{ textAlign:'center', padding:'2.5rem 1rem', color:U.muted }}>
                    <div style={{ fontSize:'2rem', marginBottom:'.5rem', opacity:.3 }}>📅</div>
                    No appointments on {weekdayShort(selDay)} {dayMonShort(selDay)}
                  </div>
                ) : groups.filter(g => g[2].length > 0).map(([gname, gtime, list]) => (
                  <div key={gname} style={{ marginBottom:'1.2rem' }}>
                    <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', marginBottom:'.55rem', padding:'0 .15rem' }}>
                      <div style={{ fontSize:'.9rem', fontWeight:700, color:U.text }}>{gname} <span style={{ fontSize:'.7rem', color:U.muted, fontWeight:500 }}>({gtime})</span></div>
                      <div style={{ fontSize:'.73rem', color:U.primary, fontWeight:600 }}>{list.length} Appointment{list.length===1?'':'s'}</div>
                    </div>
                    <div style={{ background:U.card, border:`1px solid ${U.border}`, borderRadius:16, overflow:'hidden' }}>
                      {list.map((appt, i) => {
                        const sc = STATUS_COLORS[appt.status] || STATUS_COLORS.pending
                        const isNew = (cnt[appt.email || appt.phone] || 0) <= 1
                        const cond = appt.diagnosis ? appt.diagnosis.split(/[,;]/)[0].trim() : (appt.service || 'Consultation')
                        return (
                          <button key={appt.id} onClick={() => open(appt)}
                            style={{ width:'100%', textAlign:'left', display:'flex', alignItems:'center', gap:9, padding:'.75rem .6rem', background:'none', border:'none', borderTop: i ? `1px solid ${U.border}` : 'none', cursor:'pointer', fontFamily:'inherit' }}>
                            <div style={{ width:50, flexShrink:0, textAlign:'center' }}>
                              <div style={{ fontSize:'.82rem', fontWeight:700, color:U.text, lineHeight:1.15 }}>{appt.timeSlot || appt.time || '—'}</div>
                            </div>
                            <div style={{ width:4, alignSelf:'stretch', borderRadius:4, background:sc.color, flexShrink:0 }} />
                            <div style={{ width:40, height:40, borderRadius:'50%', background:`linear-gradient(135deg,${U.primary},#60A5FA)`, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontFamily:"'DM Serif Display',serif", fontSize:'1rem', flexShrink:0 }}>{(appt.name||'?')[0].toUpperCase()}</div>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ fontSize:'.88rem', fontWeight:700, color:U.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{appt.name||'—'}</div>
                              <div style={{ fontSize:'.74rem', color:U.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{cond}</div>
                              <div style={{ fontSize:'.69rem', color:U.muted, marginTop:1 }}>{isNew ? '👤 New Patient' : '↩ Follow-up'}</div>
                            </div>
                            <div style={{ display:'flex', alignItems:'center', gap:5, flexShrink:0 }}>
                              <span style={{ fontSize:'.64rem', fontWeight:700, padding:'.2rem .5rem', borderRadius:999, background:sc.bg, color:sc.color, textTransform:'capitalize', whiteSpace:'nowrap' }}>{appt.status||'pending'}</span>
                              <span style={{ color:U.muted }}>›</span>
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </>)
            })()}
          </>)}
        </>)}

        {/* ── DESKTOP LIST ──────────────────────────────────────────────────── */}
        {listView && !isMobile && (
        <Card title={activeNav === 'Patients' ? `Patients (${filteredPatients.length})` : 'All Appointments'} id="appt-list" pad={false}
          action={
            <div style={{ display:'flex', gap:'.5rem', alignItems:'center', flexWrap:'wrap' }}>
              <div style={{ display:'flex', background:U.bg, borderRadius:999, padding:2 }}>
                {[['Appointments','Appointments'],['Patients','Patients']].map(([v,lbl]) => (
                  <button key={v} onClick={() => setActiveNav(v)}
                    style={{ padding:'.32rem .8rem', borderRadius:999, fontSize:'.72rem', fontWeight:700, border:'none', cursor:'pointer', fontFamily:'inherit', background: activeNav===v ? U.card : 'transparent', color: activeNav===v ? U.primary : U.muted, boxShadow: activeNav===v ? '0 1px 2px rgba(16,24,40,.1)' : 'none' }}>{lbl}</button>
                ))}
              </div>
              {activeNav === 'Patients' && ['All','Ongoing','Follow Up','Completed'].map(s => (
                <button key={s} onClick={() => setPatientFilter(s)}
                  style={{ padding:'.32rem .8rem', borderRadius:999, fontSize:'.72rem', fontWeight:600, border:'1px solid', cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap', borderColor: patientFilter===s ? U.primary : U.border, background: patientFilter===s ? U.primary : U.card, color: patientFilter===s ? '#fff' : U.muted }}>
                  {s} ({patientCounts[s]||0})
                </button>
              ))}
              {activeNav !== 'Patients' && ['all','pending','confirmed','completed','cancelled'].map(s => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  style={{ padding:'.32rem .8rem', borderRadius:999, fontSize:'.72rem', fontWeight:600, border:'1px solid', cursor:'pointer', fontFamily:'inherit', textTransform:'capitalize', borderColor: statusFilter===s ? U.primary : U.border, background: statusFilter===s ? U.primary : U.card, color: statusFilter===s ? '#fff' : U.muted }}>
                  {s==='all' ? `All (${counts.all})` : `${s[0].toUpperCase()+s.slice(1)} (${counts[s]||0})`}
                </button>
              ))}
            </div>
          }>
          {loading ? (
            <div style={{ textAlign:'center', padding:'3rem', color:U.muted }}>Loading…</div>
          ) : activeNav === 'Patients' ? (
            filteredPatients.length === 0 ? (
              <div style={{ textAlign:'center', padding:'3rem', color:U.muted }}>
                <div style={{ fontSize:'2rem', marginBottom:'.75rem', opacity:.25 }}>👥</div>
                No patients found
              </div>
            ) : (
              <div style={{ display:'flex', alignItems:'stretch' }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'grid', gridTemplateColumns: selPatient ? PT_NARROW : PT_WIDE, gap:'1rem', padding:'.5rem 1.25rem', borderBottom:`1px solid ${U.border}`, fontSize:'.63rem', fontWeight:700, letterSpacing:'.07em', textTransform:'uppercase', color:U.muted }}>
                    <span>Name</span>
                    <span>Last visit</span>
                    {!selPatient && <span>Treatment</span>}
                    {!selPatient && <span>Next appointment</span>}
                    <span>Status</span>
                  </div>
                  {filteredPatients.map((p, i) => {
                    const on = selPatient && selPatient.key === p.key
                    const sc = PATIENT_STATUS[p.status] || PATIENT_STATUS.Completed
                    return (
                      <div key={p.key} onClick={() => setSelPatient(on ? null : p)}
                        style={{ display:'grid', gridTemplateColumns: selPatient ? PT_NARROW : PT_WIDE, gap:'1rem', alignItems:'center', padding:'.8rem 1.25rem', borderTop: i ? `1px solid ${U.border}` : 'none', cursor:'pointer', background: on ? U.primarySoft : 'transparent', transition:'background .15s' }}
                        onMouseEnter={e => { if (!on) e.currentTarget.style.background = U.bg }}
                        onMouseLeave={e => { if (!on) e.currentTarget.style.background = 'transparent' }}>
                        <div style={{ display:'flex', gap:'.7rem', alignItems:'center', minWidth:0 }}>
                          <div style={{ width:34, height:34, borderRadius:'50%', background:`linear-gradient(135deg,${C.g700},${C.g500})`, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontFamily:"'DM Serif Display',serif", fontSize:'.85rem', flexShrink:0 }}>
                            {(p.name||'?')[0].toUpperCase()}
                          </div>
                          <div style={{ minWidth:0 }}>
                            <div style={{ fontWeight:600, color:U.text, fontSize:'.86rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.name||'\u2014'}</div>
                            <div style={{ fontSize:'.71rem', color:U.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.phone || p.email || '\u2014'}</div>
                          </div>
                        </div>
                        <div style={{ fontSize:'.78rem', color:U.text }}>{p.lastVisit ? fmtDate(p.lastVisit) : '\u2014'}</div>
                        {!selPatient && <div style={{ fontSize:'.78rem', color:U.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.service || '\u2014'}</div>}
                        {!selPatient && <div style={{ fontSize:'.78rem', color: p.nextDate ? U.text : U.muted }}>{p.nextDate ? fmtDate(p.nextDate) : 'Not booked'}</div>}
                        <div>
                          <span style={{ fontSize:'.68rem', fontWeight:700, padding:'.22rem .6rem', borderRadius:999, background:sc.bg, color:sc.color, border:`1px solid ${sc.border}`, whiteSpace:'nowrap' }}>{p.status}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
                {selPatient && (
                  <PatientPanel
                    patient={patients.find(p => p.key === selPatient.key) || selPatient}
                    onOpenAppt={open}
                    onClose={() => setSelPatient(null)} />
                )}
              </div>
            )
          ) : filtered.length === 0 ? (
            <div style={{ textAlign:'center', padding:'3rem', color:U.muted }}>
              <div style={{ fontSize:'2rem', marginBottom:'.75rem', opacity:.25 }}>📋</div>
              No appointments found
            </div>
          ) : (
            <div>
              {filtered.map((appt, i) => {
                const sc = STATUS_COLORS[appt.status] || STATUS_COLORS.pending
                return (
                  <div key={appt.id} onClick={() => open(appt)}
                    style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'1rem', padding:'1rem 1.25rem', borderTop: i ? `1px solid ${U.border}` : 'none', cursor:'pointer', transition:'background .15s' }}
                    onMouseEnter={e => e.currentTarget.style.background=U.bg}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                    <div style={{ display:'flex', gap:'.9rem', alignItems:'flex-start', flex:1, minWidth:0 }}>
                      <div style={{ width:42, height:42, borderRadius:'50%', background:`linear-gradient(135deg,${C.g700},${C.g500})`, display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontFamily:"'DM Serif Display',serif", fontSize:'1.05rem', flexShrink:0 }}>
                        {(appt.name||'?')[0].toUpperCase()}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:600, color:U.text, fontSize:'.9rem', marginBottom:'.15rem' }}>{appt.name||'—'}</div>
                        <div style={{ fontSize:'.73rem', color:U.muted, marginBottom:'.3rem' }}>{appt.email} · {appt.phone}</div>
                        <div style={{ display:'flex', gap:'.5rem', flexWrap:'wrap', alignItems:'center' }}>
                          <span style={{ fontSize:'.7rem', color:U.primary, background:U.primarySoft, padding:'.2rem .55rem', borderRadius:999 }}>📋 {appt.service||'—'}</span>
                          <span style={{ fontSize:'.7rem', color:U.muted }}>📅 {fmtDate(appt.date)}{appt.timeSlot ? ` · ${appt.timeSlot}` : ''}</span>
                        </div>
                      </div>
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:'.4rem', flexShrink:0 }}>
                      <span style={{ fontSize:'.7rem', fontWeight:600, padding:'.25rem .7rem', borderRadius:999, background:sc.bg, color:sc.color, border:`1px solid ${sc.border}`, textTransform:'capitalize', whiteSpace:'nowrap' }}>{appt.status||'pending'}</span>
                      {appt.diagnosis    && <span style={{ fontSize:'.64rem', color:U.muted,   background:U.bg,          padding:'.2rem .5rem', borderRadius:6 }}>Dx ✓</span>}
                      {appt.prescription && <span style={{ fontSize:'.64rem', color:U.primary,  background:U.primarySoft, padding:'.2rem .5rem', borderRadius:6 }}>Rx ✓</span>}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </Card>
        )}

        {/* ── AI ALERTS PAGE ─────────────────────────────────────────────────── */}
        {/* ── MOBILE ALERTS (mockup) ────────────────────────────────────────── */}
        {alertsView && isMobile && (<>
          <div style={{ padding:'1.1rem .15rem .35rem' }}>
            <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.5rem', color:U.text }}>Alerts</div>
            <div style={{ fontSize:'.82rem', color:U.muted, marginTop:2 }}>Signals from your patient data that may need attention</div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'.6rem', margin:'.85rem 0 1.1rem' }}>
            {[
              ['High', alertCounts.high, '⚠️', '#FEECEC', '#DC2626'],
              ['Medium', alertCounts.med, '❗', '#FEEFE2', '#EA580C'],
              ['Low', alertCounts.low, 'ℹ️', '#EAF1FE', '#2563EB'],
              ['Total Open', alerts.length, '🔔', '#F1EBFE', '#7C3AED'],
            ].map(([lbl,val,icon,bg,cc]) => (
              <div key={lbl} style={{ background:U.card, border:`1px solid ${U.border}`, borderRadius:14, padding:'.8rem .85rem' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:'.35rem' }}>
                  <div style={{ width:30, height:30, borderRadius:9, background:bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'.9rem' }}>{icon}</div>
                  <div style={{ fontSize:'.72rem', color:U.muted, fontWeight:600 }}>{lbl}</div>
                </div>
                <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.5rem', color:cc, lineHeight:1 }}>{loading ? '—' : val}</div>
              </div>
            ))}
          </div>

          {loading ? (
            <div style={{ textAlign:'center', padding:'3rem', color:U.muted }}>Loading alerts…</div>
          ) : filteredAlerts.length === 0 ? (
            <div style={{ textAlign:'center', padding:'3rem', color:U.muted }}><div style={{ fontSize:'2rem', marginBottom:'.5rem', opacity:.3 }}>✓</div>No alerts — everything looks on track.</div>
          ) : filteredAlerts.map(al => {
            const [pLabel,pColor,pBg] = PRIORITY[al.sev] || PRIORITY.low
            const nm = al.appt?.name || al.name || 'Patient'
            const cond = al.appt?.diagnosis ? al.appt.diagnosis.split(/[\s,]+/)[0] : (al.appt?.service || '')
            return (
              <div key={al.key} style={{ background:U.card, border:`1px solid ${U.border}`, borderLeft:`4px solid ${pColor}`, borderRadius:14, padding:'.85rem', marginBottom:'.7rem' }}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:9 }}>
                  <div style={{ width:34, height:34, borderRadius:'50%', background:pBg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'.95rem', flexShrink:0 }}>{al.icon}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                      <span style={{ fontSize:'.88rem', fontWeight:700, color:U.text, flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{al.title}</span>
                      <span style={{ fontSize:'.63rem', fontWeight:700, color:pColor, background:pBg, padding:'.15rem .5rem', borderRadius:999, flexShrink:0 }}>{pLabel}</span>
                    </div>
                    <div style={{ fontSize:'.76rem', color:U.muted, lineHeight:1.45, marginTop:'.2rem' }}>{al.text}</div>
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:'.7rem', paddingTop:'.7rem', borderTop:`1px solid ${U.border}` }}>
                  <div style={{ width:30, height:30, borderRadius:'50%', background:`linear-gradient(135deg,${U.primary},#60A5FA)`, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontFamily:"'DM Serif Display',serif", fontSize:'.82rem', flexShrink:0 }}>{nm[0].toUpperCase()}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:'.8rem', fontWeight:600, color:U.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{nm}</div>
                    <div style={{ fontSize:'.67rem', color:U.muted }}>{cond ? cond + ' · ' : ''}{fmtDate(al.detected)}</div>
                  </div>
                  {al.appt && <button onClick={() => open(al.appt)} style={{ padding:'.4rem .85rem', fontSize:'.76rem', fontWeight:700, border:`1px solid ${U.primary}`, color:U.primary, background:'#fff', borderRadius:9, cursor:'pointer', fontFamily:'inherit', flexShrink:0 }}>Review</button>}
                </div>
              </div>
            )
          })}
        </>)}

        {/* ── DESKTOP ALERTS ────────────────────────────────────────────────── */}
        {alertsView && !isMobile && (<>
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit,minmax(200px,1fr))', gap: isMobile ? '.7rem' : '1rem', marginBottom:'1.5rem' }}>
            {[
              ['High Priority',   alertCounts.high, '⚠️', '#FEECEC', 'Require immediate attention'],
              ['Medium Priority', alertCounts.med,  '❗', '#FEEFE2', 'Follow-up recommended'],
              ['Low Priority',    alertCounts.low,  'ℹ️', '#EAF1FE', 'Monitor'],
              ['Total Open',      alerts.length,    '🔔', '#F1EBFE', 'Alerts needing action'],
            ].map(([label,val,icon,bg,sub]) => (
              <div key={label} style={{ background:U.card, border:`1px solid ${U.border}`, borderRadius:16, padding:'1.1rem 1.25rem', boxShadow:'0 1px 2px rgba(16,24,40,.04)' }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:'.5rem' }}>
                  <div style={{ width:38, height:38, borderRadius:'50%', background:bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.05rem' }}>{icon}</div>
                  <div style={{ fontSize:'.78rem', color:U.muted, fontWeight:600 }}>{label}</div>
                </div>
                <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.9rem', color:U.text, lineHeight:1 }}>{loading ? '—' : val}</div>
                <div style={{ fontSize:'.7rem', color:U.muted, marginTop:'.3rem' }}>{sub}</div>
              </div>
            ))}
          </div>

          <Card pad={false}>
            {loading ? (
              <div style={{ textAlign:'center', padding:'3rem', color:U.muted }}>Loading alerts…</div>
            ) : filteredAlerts.length === 0 ? (
              <div style={{ textAlign:'center', padding:'3rem', color:U.muted }}>
                <div style={{ fontSize:'2rem', marginBottom:'.75rem', opacity:.25 }}>✓</div>
                No alerts — everything looks on track.
              </div>
            ) : (
              <div style={{ overflowX:'auto' }}>
                <div style={{ display:'grid', gridTemplateColumns:'2.2fr 1.4fr .8fr .9fr auto', gap:'1rem', padding:'.8rem 1.25rem', borderBottom:`1px solid ${U.border}`, fontSize:'.66rem', fontWeight:700, color:U.muted, textTransform:'uppercase', letterSpacing:'.05em', minWidth:820 }}>
                  <div>Alert</div><div>Patient</div><div>Priority</div><div>Detected</div><div>Actions</div>
                </div>
                {filteredAlerts.map(al => {
                  const [pLabel,pColor,pBg] = PRIORITY[al.sev] || PRIORITY.low
                  const nm = al.appt?.name || al.name || 'Patient'
                  const cond = al.appt?.diagnosis ? al.appt.diagnosis.split(/[\s,]+/)[0] : (al.appt?.service || '')
                  return (
                    <div key={al.key} style={{ display:'grid', gridTemplateColumns:'2.2fr 1.4fr .8fr .9fr auto', gap:'1rem', padding:'1rem 1.25rem', borderTop:`1px solid ${U.border}`, alignItems:'center', minWidth:820 }}>
                      <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                        <div style={{ width:30, height:30, borderRadius:'50%', background:pBg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'.85rem', flexShrink:0 }}>{al.icon}</div>
                        <div style={{ minWidth:0 }}>
                          <div style={{ fontSize:'.85rem', fontWeight:700, color:U.text }}>{al.title}</div>
                          <div style={{ fontSize:'.74rem', color:U.muted, lineHeight:1.5, marginTop:'.15rem' }}>{al.text}</div>
                          <span style={{ display:'inline-block', marginTop:'.35rem', fontSize:'.63rem', fontWeight:700, color:pColor, background:pBg, padding:'.12rem .5rem', borderRadius:6 }}>{al.tag}</span>
                        </div>
                      </div>
                      <div style={{ display:'flex', gap:8, alignItems:'center', minWidth:0 }}>
                        <div style={{ width:34, height:34, borderRadius:'50%', background:`linear-gradient(135deg,${C.g700},${C.g500})`, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontFamily:"'DM Serif Display',serif", fontSize:'.9rem', flexShrink:0 }}>{nm[0].toUpperCase()}</div>
                        <div style={{ minWidth:0 }}>
                          <div style={{ fontSize:'.8rem', fontWeight:600, color:U.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{nm}</div>
                          {cond && <div style={{ fontSize:'.7rem', color:U.muted }}>{cond}</div>}
                        </div>
                      </div>
                      <div>
                        <span title={al.conditionBased ? 'Priority from patient condition (skin-scan severity/urgency + latest progress review)' : 'Operational priority — no clinical severity on file for this patient'}
                          style={{ fontSize:'.72rem', fontWeight:700, color:pColor, background:pBg, padding:'.2rem .6rem', borderRadius:999, cursor:'help' }}>{pLabel}</span>
                        {al.conditionBased && <div style={{ fontSize:'.6rem', color:U.muted, marginTop:'.2rem' }}>by condition</div>}
                      </div>
                      <div style={{ fontSize:'.74rem', color:U.muted }}>{fmtDate(al.detected)}</div>
                      <div>
                        <button onClick={() => al.appt && open(al.appt)} disabled={!al.appt}
                          style={{ padding:'.4rem .8rem', fontSize:'.74rem', fontWeight:600, border:`1px solid ${al.appt?U.primary:U.border}`, color:al.appt?U.primary:U.muted, background:U.card, borderRadius:8, cursor: al.appt ? 'pointer':'default', fontFamily:'inherit', whiteSpace:'nowrap' }}>
                          View Patient
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </Card>
        </>)}

        {/* ── MESSAGES INBOX ─────────────────────────────────────────────────── */}
        {/* ── MOBILE MESSAGES (mockup) ──────────────────────────────────────── */}
        {messagesView && isMobile && (<>
          <div style={{ padding:'1.1rem .15rem .35rem' }}>
            <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.5rem', color:U.text }}>Messages</div>
            <div style={{ fontSize:'.82rem', color:U.muted, marginTop:2 }}>Chat with your patients</div>
          </div>

          <div style={{ display:'flex', gap:4, background:U.card, border:`1px solid ${U.border}`, borderRadius:12, padding:4, margin:'.85rem 0' }}>
            {[['all','All',messages.length],['unread','Unread',messages.filter(m=>!m.replyText).length],['replied','Replied',messages.filter(m=>m.replyText).length]].map(([k,lbl,c]) => (
              <button key={k} onClick={() => setMsgTab(k)}
                style={{ flex:1, padding:'.5rem', borderRadius:9, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:'.79rem', fontWeight:700, background: msgTab===k ? U.primarySoft : 'transparent', color: msgTab===k ? U.primary : U.muted }}>
                {lbl}{c ? ` (${c})` : ''}
              </button>
            ))}
          </div>

          <div style={{ position:'relative', marginBottom:'.9rem' }}>
            <span style={{ position:'absolute', left:'.85rem', top:'50%', transform:'translateY(-50%)', color:U.muted, fontSize:'.85rem' }}>🔍</span>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search messages…"
              style={{ width:'100%', padding:'.7rem .9rem .7rem 2.3rem', border:`1px solid ${U.border}`, borderRadius:12, fontSize:'.85rem', outline:'none', fontFamily:'inherit', background:U.card, boxSizing:'border-box', color:U.text }} />
          </div>

          {(() => {
            let list = messages
            if (msgTab === 'unread') list = list.filter(m => !m.replyText)
            else if (msgTab === 'replied') list = list.filter(m => m.replyText)
            if (search) list = list.filter(m => (m.patientName || m.patientEmail || '').toLowerCase().includes(search.toLowerCase()) || (m.feedbackText || '').toLowerCase().includes(search.toLowerCase()))
            if (list.length === 0) return (
              <div style={{ textAlign:'center', padding:'3rem', color:U.muted }}>
                <div style={{ fontSize:'2rem', marginBottom:'.5rem', opacity:.3 }}>💬</div>No messages
              </div>
            )
            return (
              <div style={{ background:U.card, border:`1px solid ${U.border}`, borderRadius:16, overflow:'hidden' }}>
                {list.map((m, i) => {
                  const unread = !m.replyText
                  const nm = m.patientName || m.patientEmail || 'Patient'
                  const cond = typeof conditionByEmail[m.patientEmail] === 'string' ? conditionByEmail[m.patientEmail] : ''
                  return (
                    <button key={m.id} onClick={() => openMsg(m)}
                      style={{ width:'100%', textAlign:'left', display:'flex', gap:10, alignItems:'flex-start', padding:'.85rem .8rem', borderTop: i ? `1px solid ${U.border}` : 'none', cursor:'pointer', background:'none', border:'none', fontFamily:'inherit' }}>
                      <div style={{ position:'relative', flexShrink:0 }}>
                        <div style={{ width:44, height:44, borderRadius:'50%', background:`linear-gradient(135deg,${U.primary},#60A5FA)`, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontFamily:"'DM Serif Display',serif", fontSize:'1.05rem' }}>{nm[0].toUpperCase()}</div>
                        <span style={{ position:'absolute', top:0, left:0, width:11, height:11, borderRadius:'50%', border:'2px solid #fff', background: unread ? U.primary : '#CBD5E1' }} />
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                          <span style={{ fontSize:'.9rem', fontWeight:700, color:U.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{nm}</span>
                          {unread && <span style={{ fontSize:'.6rem', fontWeight:700, color:U.primary, background:U.primarySoft, padding:'.05rem .4rem', borderRadius:6, flexShrink:0 }}>New</span>}
                          {m.mediaUrl && <span style={{ fontSize:'.7rem' }}>📎</span>}
                          <span style={{ marginLeft:'auto', fontSize:'.67rem', color:U.muted, flexShrink:0 }}>{timeAgo(m.createdAt)}</span>
                        </div>
                        {cond && <div style={{ fontSize:'.72rem', color:U.muted }}>{cond}</div>}
                        <div style={{ fontSize:'.8rem', color: unread ? U.text : U.muted, marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontWeight: unread ? 600 : 400 }}>{m.feedbackText || '(media only)'}</div>
                      </div>
                    </button>
                  )
                })}
              </div>
            )
          })()}
        </>)}

        {/* ── DESKTOP MESSAGES ──────────────────────────────────────────────── */}
        {messagesView && !isMobile && (
          <Card title={`Inbox${unreadCount ? ` · ${unreadCount} unread` : ''}`} pad={false}
            action={<span style={{ fontSize:'.66rem', color:U.muted }}>patient check-ins</span>}>
            {(() => {
              const list = search ? messages.filter(m => (m.patientName || m.patientEmail || '').toLowerCase().includes(search.toLowerCase()) || (m.feedbackText || '').toLowerCase().includes(search.toLowerCase())) : messages
              if (list.length === 0) return (
                <div style={{ textAlign:'center', padding:'3rem', color:U.muted }}>
                  <div style={{ fontSize:'2rem', marginBottom:'.75rem', opacity:.25 }}>💬</div>
                  No messages yet.
                </div>
              )
              return (
                <div>
                  {list.map((m, i) => {
                    const unread = !m.replyText
                    const nm = m.patientName || m.patientEmail || 'Patient'
                    return (
                      <button key={m.id} onClick={() => openMsg(m)}
                        style={{ width:'100%', textAlign:'left', display:'flex', gap:'.9rem', alignItems:'flex-start', padding:'1rem 1.25rem', borderTop: i ? `1px solid ${U.border}` : 'none', cursor:'pointer', background: unread ? '#FBFCFF' : 'none', border:'none', fontFamily:'inherit' }}>
                        <div style={{ width:40, height:40, borderRadius:'50%', background:`linear-gradient(135deg,${C.g700},${C.g500})`, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontFamily:"'DM Serif Display',serif", fontSize:'1rem', flexShrink:0 }}>{nm[0].toUpperCase()}</div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <span style={{ fontSize:'.85rem', fontWeight:700, color:U.text }}>{nm}</span>
                            {m.type === 'weekly' && <span style={{ fontSize:'.6rem', color:U.muted, background:U.bg, padding:'.08rem .4rem', borderRadius:6 }}>weekly check-in</span>}
                            {m.mediaUrl && <span style={{ fontSize:'.65rem' }}>📎</span>}
                            <span style={{ marginLeft:'auto', fontSize:'.68rem', color:U.muted }}>{timeAgo(m.createdAt)}</span>
                          </div>
                          <div style={{ fontSize:'.8rem', color: unread ? U.text : U.muted, marginTop:'.25rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {m.feedbackText || '(no text — media only)'}
                          </div>
                          <div style={{ marginTop:'.3rem' }}>
                            {unread
                              ? <span style={{ fontSize:'.64rem', fontWeight:700, color:U.primary, background:U.primarySoft, padding:'.12rem .5rem', borderRadius:6 }}>Needs reply</span>
                              : <span style={{ fontSize:'.64rem', color:'#2F9E44' }}>✓ Replied</span>}
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )
            })()}
          </Card>
        )}
      </main>

      {/* ── MOBILE BOTTOM TAB BAR ───────────────────────────────────────────── */}
      {isMobile && (
        <div style={{ position:'fixed', bottom:0, left:0, right:0, zIndex:1000, background:U.card, borderTop:`1px solid ${U.border}`, display:'flex', boxShadow:'0 -2px 14px rgba(16,24,40,.08)', paddingBottom:'env(safe-area-inset-bottom)' }}>
          {[
            ['Dashboard','🏠'],['Patients','👥'],['Appointments','📅'],['Alerts','🔔'],['Messages','💬'],
          ].map(([label,icon]) => {
            const on = activeNav === label
            return (
              <button key={label} onClick={() => onNav(label)}
                style={{ position:'relative', flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:4, padding:'.6rem 0 .65rem', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', color: on ? U.primary : U.muted }}>
                {on && <span style={{ position:'absolute', top:0, width:26, height:3, borderRadius:3, background:U.primary }} />}
                <span style={{ position:'relative', fontSize:'1.15rem' }}>{icon}
                  {label === 'Alerts' && alerts.length > 0 && (
                    <span style={{ position:'absolute', top:-6, right:-10, fontSize:'.54rem', fontWeight:700, color:'#fff', background:'#EF4444', borderRadius:999, padding:'.02rem .3rem' }}>{alerts.length}</span>
                  )}
                  {label === 'Messages' && unreadCount > 0 && (
                    <span style={{ position:'absolute', top:-6, right:-10, fontSize:'.54rem', fontWeight:700, color:'#fff', background:U.primary, borderRadius:999, padding:'.02rem .3rem' }}>{unreadCount}</span>
                  )}
                </span>
                <span style={{ fontSize:'.62rem', fontWeight: on ? 700 : 500 }}>{label}</span>
              </button>
            )
          })}
        </div>
      )}

      {/* MODAL POPUP */}
      {selected && (
        <div
          onClick={e => { if (e.target === e.currentTarget) setSelected(null) }}
          style={{ position:'fixed', inset:0, zIndex:1000, background:'rgba(0,0,0,.55)', display:'flex', alignItems:'center', justifyContent:'center', padding:'1.5rem' }}>
          <div style={{ width:'100%', maxWidth:720, maxHeight:'90vh', display:'flex', flexDirection:'column', background:'white', borderRadius:20, overflow:'hidden', boxShadow:'0 24px 64px rgba(13,34,24,.25)' }}>

            {/* Modal Header */}
            <div style={{ background:C.headerGrad, padding:'1.5rem', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
              <div style={{ display:'flex', gap:12, alignItems:'center' }}>
                <div style={{ width:48, height:48, borderRadius:'50%', background:'rgba(255,255,255,.15)', border:'2px solid rgba(255,255,255,.25)', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'DM Serif Display',serif", fontSize:'1.3rem', color:'white' }}>
                  {(selected.name||'?')[0].toUpperCase()}
                </div>
                <div>
                  <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.2rem', color:'white' }}>{selected.name}</div>
                  <div style={{ fontSize:'.75rem', color:'rgba(255,255,255,.55)' }}>{selected.email}</div>
                </div>
              </div>
              <button onClick={() => setSelected(null)} style={{ width:36, height:36, borderRadius:8, background:'rgba(255,255,255,.1)', border:'1px solid rgba(255,255,255,.2)', color:'rgba(255,255,255,.85)', cursor:'pointer', fontSize:'1.4rem', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'DM Sans',sans-serif", lineHeight:1 }}>×</button>
            </div>

            {/* Modal Scrollable Body */}
            <div style={{ overflowY:'auto', flex:1 }}>

              {/* Info Grid */}
              <div style={{ padding:'1.25rem 1.5rem', borderBottom:`1px solid ${C.border}`, background:C.g50 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'.6rem' }}>
                  {[['📋 Service',selected.service||'—'],['📅 Date',fmtDate(selected.date)],['🕐 Time',selected.timeSlot||'—'],['📞 Phone',selected.phone||'—'],['💻 Mode',selected.mode||'clinic']].map(([l,v]) => (
                    <div key={l} style={{ background:'white', borderRadius:8, padding:'.6rem .75rem', border:`1px solid ${C.border}` }}>
                      <div style={{ fontSize:'.65rem', color:C.muted, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:'.2rem' }}>{l}</div>
                      <div style={{ fontSize:'.82rem', fontWeight:500, color:C.text }}>{v}</div>
                    </div>
                  ))}
                </div>
                {(selected.mode === 'online' || selected.meetLink) && (
                  <div style={{ marginTop:'.75rem' }}>
                    <a
                      href={selected.meetLink || `https://meet.jit.si/revive-${selected.id}`}
                      target="_blank" rel="noopener noreferrer"
                      style={{ display:'inline-flex', alignItems:'center', gap:8, background:'#1A73E8', color:'white', padding:'.65rem 1.25rem', borderRadius:9, fontSize:'.85rem', fontWeight:600, textDecoration:'none', boxShadow:'0 2px 8px rgba(26,115,232,.3)', transition:'all .2s' }}
                      onMouseEnter={e => e.currentTarget.style.background='#1557B0'}
                      onMouseLeave={e => e.currentTarget.style.background='#1A73E8'}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M17 10.5V7a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h12a1 1 0 001-1v-3.5l4 4v-11l-4 4z"/></svg>
                      Join Video Call
                    </a>
                  </div>
                )}
              </div>

              {/* AI Follow-up Call Button */}
              <div style={{ padding:'.75rem 1.5rem', borderBottom:`1px solid ${C.border}`, background:'#F5F3FF', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div style={{ fontSize:'.8rem', color:'#6D28D9', fontWeight:600 }}>🤖 AI Follow-up Call <span style={{ fontWeight:400, color:'#9CA3AF', fontSize:'.72rem' }}>(Bolna + Sarvam)</span></div>
                <button
                  onClick={async () => {
                    if (!window.confirm(`Call ${selected.name || 'patient'} (${selected.phone}) for AI follow-up?`)) return
                    try {
                      const res = await fetchWithAuth(`${API_BASE}/api/voice/followup-call`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          appointmentId: selected.id,
                          phone: selected.phone,
                          name: selected.name,
                          diagnosis: selected.diagnosis,
                          medication: selected.medication
                        })
                      })
                      const data = await res.json()
                      if (!res.ok) throw new Error(data.message || 'Failed')
                      alert(`✅ AI follow-up call initiated! ${selected.name} will receive a call shortly.`)
                    } catch(e) {
                      alert('Error: ' + e.message)
                    }
                  }}
                  style={{ padding:'.5rem 1.1rem', borderRadius:8, background:'#7C3AED', color:'white', border:'none', cursor:'pointer', fontSize:'.82rem', fontWeight:600 }}
                >
                  📞 Call Patient
                </button>
              </div>

              {/* Patient Photos */}
              <PhotosSection photos={selected.photos} apiBase={API_BASE} border={C.border} muted={C.muted} />

              {/* AI Skin Scan */}
              <SkinScanSection patientEmail={selected.email} apiBase={API_BASE} fetchWithAuth={fetchWithAuth} appointmentDate={selected.date} appointmentTime={selected.time} />

              {/* 2-Week Progress Photos */}
              <ProgressPhotosSection progressPhotos={selected.progressPhotos} apptDate={selected.date} apptId={selected.id} apiBase={API_BASE} border={C.border} fetchWithAuth={fetchWithAuth} onReviewed={() => { loadPending(); loadAlerts() }} />

              {/* Patient's standalone prescription uploads */}
              <StandaloneRxSection patientEmail={selected.email} apiBase={API_BASE} fetchWithAuth={fetchWithAuth} border={C.border} />

              {/* Editable Fields */}
              <div style={{ padding:'1.5rem', display:'flex', flexDirection:'column', gap:'1.1rem' }}>

                <div>
                  <label style={{ fontSize:'.7rem', fontWeight:600, color:C.muted, textTransform:'uppercase', letterSpacing:'.08em', display:'block', marginBottom:'.4rem' }}>Status</label>
                  <select value={form.status} onChange={e => setForm(f => ({...f, status:e.target.value}))} style={{ ...inp, resize:'none' }}>
                    {['pending','confirmed','completed','cancelled'].map(s => <option key={s} value={s}>{s[0].toUpperCase()+s.slice(1)}</option>)}
                  </select>
                </div>

                <div>
                  <label style={{ fontSize:'.7rem', fontWeight:600, color:C.muted, textTransform:'uppercase', letterSpacing:'.08em', display:'block', marginBottom:'.4rem' }}>🔬 Diagnosis</label>
                  <textarea rows={3} value={form.diagnosis} onChange={e => setForm(f => ({...f, diagnosis:e.target.value}))} placeholder="Enter diagnosis notes…" style={inp}
                    onFocus={e => e.target.style.borderColor=C.g500} onBlur={e => e.target.style.borderColor=C.border} />
                </div>

                <div>
                  <label style={{ fontSize:'.7rem', fontWeight:600, color:C.muted, textTransform:'uppercase', letterSpacing:'.08em', display:'block', marginBottom:'.4rem' }}>💊 Prescription / Treatment Plan</label>
                  <textarea rows={Math.min(30, Math.max(6, (form.prescription||'').split('\n').length + 1))} value={form.prescription} onChange={e => setForm(f => ({...f, prescription:e.target.value}))} placeholder={'e.g. Tretinoin 0.025% — apply at night\nNiacinamide serum — morning\nSPF50 sunscreen — daily'} style={{ ...inp, resize:'vertical' }}
                    onFocus={e => e.target.style.borderColor=C.g500} onBlur={e => e.target.style.borderColor=C.border} />
                </div>

                <div>
                  <label style={{ fontSize:'.7rem', fontWeight:600, color:C.muted, textTransform:'uppercase', letterSpacing:'.08em', display:'block', marginBottom:'.4rem' }}>🏥 Medication (for reminders)</label>
                  <textarea rows={Math.min(30, Math.max(4, (form.medication||'').split('\n').length + 1))} value={form.medication} onChange={e => setForm(f => ({...f, medication:e.target.value}))} placeholder={'e.g. Doxycycline 100mg — 9am\nClindamycin gel — 9pm'} style={{ ...inp, resize:'vertical' }}
                    onFocus={e => e.target.style.borderColor=C.g500} onBlur={e => e.target.style.borderColor=C.border} />
                  <div style={{ fontSize:'.7rem', color:C.muted, marginTop:'.3rem' }}>⏰ Include time (9am, 9pm) to enable medication reminders</div>
                </div>

                <div>
                  <label style={{ fontSize:'.7rem', fontWeight:600, color:C.muted, textTransform:'uppercase', letterSpacing:'.08em', display:'block', marginBottom:'.4rem' }}>📅 Follow-up Date</label>
                  <input type="date" value={form.followUpDate} onChange={e => setForm(f => ({...f, followUpDate:e.target.value}))} style={{ ...inp, resize:'none' }}
                    onFocus={e => e.target.style.borderColor=C.g500} onBlur={e => e.target.style.borderColor=C.border} />
                </div>

                <button onClick={save} disabled={saving}
                  style={{ width:'100%', padding:'.9rem', borderRadius:10, background:saving ? C.g400 : C.g700, color:'white', border:'none', fontSize:'.9rem', fontWeight:600, cursor:saving?'not-allowed':'pointer', fontFamily:"'DM Sans',sans-serif", letterSpacing:'.04em', boxShadow:`0 4px 16px rgba(35,82,51,.25)`, display:'flex', alignItems:'center', justifyContent:'center', gap:9, transition:'all .2s' }}
                  onMouseEnter={e => { if (!saving) e.currentTarget.style.background=C.g800 }}
                  onMouseLeave={e => { if (!saving) e.currentTarget.style.background=C.g700 }}>
                  {saving
                    ? <><span style={{ width:15, height:15, borderRadius:'50%', border:'2px solid rgba(255,255,255,.3)', borderTopColor:'white', display:'inline-block', animation:'spin .8s linear infinite' }} /> Saving…</>
                    : '💾 Save Changes'}
                </button>

                {saveMsg === 'error' && (
                  <div style={{ padding:'.7rem 1rem', borderRadius:9, textAlign:'center', fontSize:'.82rem', fontWeight:600, background:'#FFF0F0', color:C.error, border:`1px solid #FFC9C9` }}>
                    ✗ Save failed. Try again.
                  </div>
                )}

                <button onClick={() => setPatientModal(selected.email)}
                  style={{ width:'100%', padding:'.7rem', borderRadius:9, background:C.g50, color:C.g700, border:`1px solid ${C.border}`, fontSize:'.8rem', fontWeight:500, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}
                  onMouseEnter={e => { e.currentTarget.style.background=C.g100 }}
                  onMouseLeave={e => { e.currentTarget.style.background=C.g50 }}>
                  👤 View Full Patient History
                </button>

                {selected.message && (
                  <div style={{ background:C.g50, border:`1px solid ${C.border}`, borderRadius:10, padding:'.9rem 1rem' }}>
                    <div style={{ fontSize:'.68rem', fontWeight:600, color:C.muted, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:'.4rem' }}>Patient Notes</div>
                    <div style={{ fontSize:'.82rem', color:C.text, lineHeight:1.6 }}>{selected.message}</div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      )}

      {/* PATIENT DETAILS MODAL */}
      {patientModal && (
        <div onClick={e => { if (e.target === e.currentTarget) setPatientModal(null) }}
          style={{ position:'fixed', inset:0, zIndex:1350, background:'rgba(0,0,0,.55)', display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'1.25rem', overflowY:'auto' }}>
          <PatientDetails emailProp={patientModal} onClose={() => setPatientModal(null)} />
        </div>
      )}

      {/* MESSAGE REPLY MODAL */}
      {selectedMsg && (
        <div onClick={e => { if (e.target === e.currentTarget) setSelectedMsg(null) }}
          style={{ position:'fixed', inset:0, zIndex:1300, background:'rgba(0,0,0,.55)', display:'flex', alignItems:'center', justifyContent:'center', padding:'1.25rem' }}>
          <div style={{ width:'100%', maxWidth:560, maxHeight:'90vh', display:'flex', flexDirection:'column', background:'#fff', borderRadius:18, overflow:'hidden', boxShadow:'0 24px 64px rgba(13,34,24,.25)' }}>
            <div style={{ background:C.headerGrad, padding:'1.1rem 1.25rem', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                <div style={{ width:40, height:40, borderRadius:'50%', background:'rgba(255,255,255,.15)', border:'2px solid rgba(255,255,255,.25)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontFamily:"'DM Serif Display',serif" }}>{(selectedMsg.patientName || selectedMsg.patientEmail || '?')[0].toUpperCase()}</div>
                <div>
                  <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.05rem', color:'#fff' }}>{selectedMsg.patientName || 'Patient'}</div>
                  <div style={{ fontSize:'.7rem', color:'rgba(255,255,255,.6)' }}>{selectedMsg.patientEmail}</div>
                </div>
              </div>
              <button onClick={() => setSelectedMsg(null)} style={{ width:34, height:34, borderRadius:8, background:'rgba(255,255,255,.1)', border:'1px solid rgba(255,255,255,.2)', color:'#fff', cursor:'pointer', fontSize:'1.3rem', lineHeight:1 }}>×</button>
            </div>
            <div style={{ overflowY:'auto', padding:'1.25rem', display:'flex', flexDirection:'column', gap:'1rem' }}>
              <div>
                <div style={{ fontSize:'.65rem', fontWeight:700, color:U.muted, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'.35rem' }}>Patient wrote · {timeAgo(selectedMsg.createdAt)}</div>
                <div style={{ background:U.bg, borderRadius:12, padding:'.85rem 1rem', fontSize:'.86rem', color:U.text, lineHeight:1.6, whiteSpace:'pre-wrap' }}>{selectedMsg.feedbackText || '(no text — media only)'}</div>
                {selectedMsg.mediaUrl && (
                  <a href={selectedMsg.mediaUrl.startsWith('http') ? selectedMsg.mediaUrl : `${API_BASE}${selectedMsg.mediaUrl}`} target="_blank" rel="noopener noreferrer"
                    style={{ display:'inline-block', marginTop:'.5rem', fontSize:'.75rem', color:U.primary, fontWeight:600 }}>📎 View attached media</a>
                )}
              </div>
              <div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'.4rem' }}>
                  <div style={{ fontSize:'.65rem', fontWeight:700, color:U.muted, textTransform:'uppercase', letterSpacing:'.06em' }}>Your reply</div>
                  <button onClick={aiDraft} disabled={aiDrafting}
                    style={{ fontSize:'.7rem', fontWeight:600, color:'#7C3AED', background:'#F5F3FF', border:'1px solid #E9D8FD', borderRadius:7, padding:'.25rem .6rem', cursor:'pointer', fontFamily:'inherit' }}>
                    {aiDrafting ? '✨ Drafting…' : '✨ AI draft'}
                  </button>
                </div>
                <textarea value={replyText} onChange={e => setReplyText(e.target.value)} rows={5} placeholder="Write a reply to your patient…"
                  style={{ width:'100%', padding:'.7rem .85rem', border:`1px solid ${U.border}`, borderRadius:10, fontSize:'.85rem', fontFamily:'inherit', resize:'vertical', outline:'none', boxSizing:'border-box', lineHeight:1.6, color:U.text }} />
                <div style={{ fontSize:'.66rem', color:U.muted, marginTop:'.3rem' }}>AI draft is a suggestion — review and edit before sending.</div>
              </div>
              <button onClick={sendReply} disabled={replySaving || !replyText.trim()}
                style={{ width:'100%', padding:'.85rem', borderRadius:10, background: replyText.trim() ? C.g700 : '#ccc', color:'#fff', border:'none', fontSize:'.88rem', fontWeight:700, cursor: replyText.trim() && !replySaving ? 'pointer' : 'default', fontFamily:'inherit' }}>
                {replySaving ? 'Sending…' : selectedMsg.replyText ? 'Update reply' : 'Send reply'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        @keyframes spin { to { transform: rotate(360deg) } }
      `}</style>
    </div>
  )
}
